import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { devLog, devWarn } from '../utils/devLog';
import { useNavigate } from 'react-router-dom';
import { useRouteData } from './SafeRoute';
import { workflowService, WorkflowData } from '../services/workflowService';
import { jobsService } from '../services/jobsService';
import { useBackgroundReports } from '../contexts/BackgroundReportContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { WorkflowSelector } from './UniversalJsonRenderer';
import ChatInterface from './ChatInterfaceQuoteUpdate';
import {
  ToolExecution,
  SavePoint,
  PolicyComparison,
  ApplicationUpload,
  PreviousApplicationUpload,
  NewApplicationUpload,
  ApplicationComparison,
  SaveExportPoint,
  ResearchTool,
  ResearchUrlInput,
  ResearchExecution,
  ComparisonTypeSelection,
  DocumentSourceSelection,
  LossRunAnalysis,
  LossRunAnalyzer,
  QuoteComparisonTool,
  YearOverYearTool,
  AdditionalContextStep,
  ReportOptionsStep,
  RenewalAnalysis,
  YearOverYearAnalysis,
  InsuranceRequirementsAnalysis,
  FinancialAnalysis,
  ClientRenewalPresentation,
  RateAnalysisExecution,
  PortfolioAnalyzerExecution,
  HomeownersComparisonExecution,
  PersonalUmbrellaExecution,
  CyberSuiteExecution
} from './WorkflowSteps';
// DocumentSelection deprecated - using DocumentSourceSelection from WorkflowSteps.tsx
import {
  ComparisonDocumentUpload,
  ComparisonAdditionalContext,
  ComparisonExecution
} from './DocumentComparisonWizard';
import CertificateOfEffortGeneration from './CertificateOfEffortGeneration';
import PastDocumentSelector from './PastDocumentSelector';
import PersonalUmbrellaQuestionnaire from './PersonalUmbrellaQuestionnaire';
import FileUploader from './FileUploader';
import RouterGoalStep from './RouterGoalStep';
import { BETA_TOOL_LABELS } from '../constants/betaToolLabels';
import { useWorkflowView } from '../contexts/WorkflowViewContext';

// Standard accepted file types
const STANDARD_ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'text/csv', // .csv
  'message/rfc822', // .eml
  'application/vnd.ms-outlook' // .msg
];

// Format workflow type slug into display title with correct casing
const formatWorkflowTitle = (type: string): string => {
  if (BETA_TOOL_LABELS[type]) return BETA_TOOL_LABELS[type];
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
    .replace(/\bYoy\b/g, 'YOY')
    .replace(/\bOf\b/g, 'of')
    .replace(/\bAnd\b/g, 'and')
    .replace(/^./, l => l.toUpperCase());
};

interface WorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowType: 'new_business' | 'renewal' | 'document_comparison' | 'loss_run_analysis' | 'loss_run_analyzer' | 'loss_run_analyzer_v4' | 'loss_run_analyzer_v4_3' | 'year_over_year' | 'yoy_renewal' | 'personal_lines_yoy_renewal' | 'commercial_lines_exec_renewal' | 'client_renewal_presentation' | 'research_tool' | 'universal_analysis' | 'certificate_of_effort' | 'coverage_analysis' | 'insurance_requirements' | 'financial_analysis' | 'rate_analysis' | 'portfolio_analyzer' | 'homeowners_comparison' | 'personal_umbrella' | 'cyber_auto' | 'cyber_benchmark' | 'cyber_renewal_delta' | 'cyber_war_systemic' | 'cyber_ransomware_trap' | 'cyber_quote_comparison' | 'do_auto' | 'do_benchmark' | 'do_renewal_delta' | 'do_distress_scanner' | 'do_emerging_exposure' | 'home_auto' | 'home_owner_benchmark' | 'home_tenant_condo_benchmark' | 'home_quote_comparison' | 'home_renewal_delta' | 'home_dp_rental_benchmark' | 'home_dp_rental_renewal_delta' | 'home_hnw_benchmark' | 'plines_auto' | 'pa_benchmark' | 'pa_quote_comparison' | 'pa_renewal_delta' | 'umb_benchmark' | 'umb_quote_comparison' | 'umb_renewal_delta' | 'rt_auto' | 'rt_requirements_reviewer' | 'rt_conformance_alignment' | 'rt_compliance_verifier' | 'premdev_auto' | 'premdev_workers_comp' | 'premdev_prof_liability' | 'premdev_general_liability' | 'premdev_property' | 'premdev_package_bop' | 'lossrun_auto' | 'lossrun_fact_engine' | 'lossrun_mitigation_wc' | 're_auto' | 're_policy_extraction' | 're_risk_transfer' | 're_br_financials' | 're_zone_extractor' | 're_portfolio_brief' | 'cl_auto' | 'cl_gl_benchmark' | 'cl_property_benchmark' | 'cl_commercial_auto_benchmark' | 'cl_inland_marine_benchmark' | 'cl_excess_benchmark' | 'cl_package_benchmark' | 'cl_bop_benchmark' | 'cl_nytpao_flow' | 'cl_package_quote_comparison' | 'cl_bop_quote_comparison' | 'cl_xarch_quote_comparison' | 'cl_package_renewal_delta' | 'cl_bop_renewal_delta' | 'ca_auto' | 'ca_coverage_determination' | 'ca_portfolio_determination' | 'ca_carrier_position_audit' | 'ntr_auto' | 'ntr_first_party' | 'ntr_property_liability' | 'ntr_mgmt_professional';
  clientId: string;
  clientName: string;
  clientType: 'customer' | 'prospect' | 'suspect';
  contactName?: string;
  projectId?: string;
  projectName?: string;
  resumeData?: any;
  onWorkflowSwitch?: (newWorkflowType: string) => void;
  // Pre-fill from a Phase-3 "Recommended for this project" workflow suggestion.
  initialSelectedDocumentIds?: string[];
  initialContext?: string;
}

interface WorkflowStep {
  id: string;
  title: string;
  component: React.ReactNode;
  canProceed: boolean;
  /** Overrides the footer's default "Next" label for this step */
  nextLabel?: string;
}

const WorkflowModal: React.FC<WorkflowModalProps> = ({
  isOpen,
  onClose,
  workflowType: requestedWorkflowType,
  clientId,
  clientName,
  clientType,
  contactName,
  projectId,
  projectName,
  resumeData,
  onWorkflowSwitch,
  initialSelectedDocumentIds,
  initialContext
}) => {
  // Production view: a suggestion or pinned link to a suite's individual tool
  // opens the suite's auto tool (resumed runs keep their original tool).
  const { launchableType } = useWorkflowView();
  const workflowType = (resumeData ? requestedWorkflowType : launchableType(requestedWorkflowType)) as WorkflowModalProps['workflowType'];
  const [currentStep, setCurrentStep] = useState(0);
  const [workflowData, setWorkflowData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [chatContext, setChatContext] = useState<any[]>([]);
  const [savedWorkflowId, setSavedWorkflowId] = useState<string | null>(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [reportTitle, setReportTitle] = useState('');
  const navigate = useNavigate();
  const { storeData } = useRouteData('workflow-report-data');

  // Background workflow state
  const { addBackgroundReport, hasActiveBackgroundReport, removeReport, refreshReports } = useBackgroundReports();
  const { showToast } = useToast();
  const { token } = useAuth();
  const [executionStartTime, setExecutionStartTime] = useState<Date | null>(null);
  const [showBackgroundOption, setShowBackgroundOption] = useState(false);
  const [currentWorkflowId, setCurrentWorkflowId] = useState<string | null>(null);
  const backgroundTimerRef = useRef<NodeJS.Timeout | null>(null);
  const backgroundedWorkflowIdRef = useRef<string | null>(null); // Track which workflow was sent to background
  const isOpenRef = useRef(isOpen); // Track isOpen in a ref to avoid stale closures in completion handlers
  const BACKGROUND_TIMER_MS = 3 * 60 * 1000; // 3 minutes (legacy - used as fallback)

  // Keep isOpenRef in sync with isOpen prop
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (resumeData) {
        setWorkflowData(resumeData);
        // Route to appropriate step based on workflow type
        setCurrentStep(getResumeStep(workflowType));
      } else {
        setWorkflowData({
          initialSelectedDocumentIds: initialSelectedDocumentIds || undefined,
          initialContext: initialContext || undefined,
        });
        setCurrentStep(0);
      }
      setError(null);
      setShowChat(false);
      setChatContext([]);
      setReportTitle(''); // Reset report title for new workflow
      // Reset background workflow state for new workflow
      setExecutionStartTime(null);
      setShowBackgroundOption(false);
      setCurrentWorkflowId(null);
      // Note: Don't reset backgroundedWorkflowIdRef - old backgrounded workflows may still complete
    }

    // Cleanup background timer when modal closes
    return () => {
      if (backgroundTimerRef.current) {
        clearTimeout(backgroundTimerRef.current);
        backgroundTimerRef.current = null;
      }
    };
  }, [isOpen, resumeData, workflowType, initialSelectedDocumentIds, initialContext]);

  // Catalog launches (no Recommended-card preselection) still consult the
  // suggestion engine — the same matched documents/context a card would
  // have preselected. Non-blocking: a failure here just leaves manual
  // document selection, same as before this existed. Runs once per modal
  // open (only when the caller didn't already supply a preselection).
  const [catalogSuggestionsLoading, setCatalogSuggestionsLoading] = useState(false);

  // Express launch: a "Run with these documents" click from a Recommended card
  // supplies initialSelectedDocumentIds synchronously, so we can skip straight
  // past the doc-selection and context steps once each becomes satisfied —
  // cutting the happy path to card-click + one Run click. Catalog launches
  // (which only get a suggestion match asynchronously, if at all) keep the
  // full wizard. Previous/back-navigation cancels express mode so it never
  // fights a user who wants to adjust the selection.
  const [expressMode, setExpressMode] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setExpressMode(!resumeData && !!(initialSelectedDocumentIds && initialSelectedDocumentIds.length > 0));
    } else {
      setExpressMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || resumeData || initialSelectedDocumentIds || !projectId) return;

    let cancelled = false;
    setCatalogSuggestionsLoading(true);
    (async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
        const res = await fetch(`${API_BASE_URL}/api/workflows/suggestions/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const suggestions: Array<{ workflowType: string; documentIds: string[]; prefillContext: string; score: number }> = await res.json();

        const matches = suggestions.filter((s) => s.workflowType === workflowType);
        if (matches.length === 0 || cancelled) return;
        const best = matches.reduce((a, b) => (b.score > a.score ? b : a));

        setWorkflowData((prev: any) => ({
          ...prev,
          initialSelectedDocumentIds: best.documentIds,
          initialContext: best.prefillContext,
        }));
      } catch {
        // Non-critical — the workflow still works with manual document selection.
      } finally {
        if (!cancelled) setCatalogSuggestionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId, workflowType]);

  const getResumeStep = (type: string): number => {
    switch (type) {
      case 'new_business':
        return 5; // Go to Quote Comparison Tool
      case 'renewal':
        return 4; // Go to Year over Year Tool
      default:
        return 0;
    }
  };

  const saveWorkflow = async (stage: string, data: any) => {
    try {
      const workflowDataToSave: WorkflowData = {
        clientId,
        projectId,
        workflowType,
        workflowStage: stage,
        data: { ...workflowData, ...data },
        timestamp: new Date()
      };

      const workflowId = await workflowService.saveWorkflow(workflowDataToSave);
      setSavedWorkflowId(workflowId);
      setWorkflowData((prev: any) => ({ ...prev, ...data }));
      return workflowId;
    } catch (error) {
      console.error('Error saving workflow:', error);
      throw error;
    }
  };

  const exportReport = async (report: any, filename: string) => {
    try {
      await workflowService.exportReport(report, filename);
    } catch (error) {
      console.error('Error exporting report:', error);
      setError('Failed to export report');
    }
  };

  const getSteps = (): WorkflowStep[] => {
    switch (workflowType) {
      case 'new_business':
        return getNewBusinessSteps();
      case 'renewal':
        return getRenewalSteps();
      case 'document_comparison':
        return getDocumentComparisonSteps();
      case 'loss_run_analysis':
        return getLossRunAnalysisSteps();
      case 'loss_run_analyzer':
        return getLossRunAnalyzerSteps();
      case 'loss_run_analyzer_v4':
        return getLossRunAnalyzerV4Steps();
      case 'loss_run_analyzer_v4_3':
        return getLossRunAnalyzerV4_3Steps();
      case 'year_over_year':
        return getYearOverYearSteps();
      case 'yoy_renewal':
        return getYoyRenewalSteps();
      case 'personal_lines_yoy_renewal':
        return getPersonalLinesYoyRenewalSteps();
      case 'commercial_lines_exec_renewal':
        return getCommercialLinesExecRenewalSteps();
      case 'research_tool':
        return getResearchToolSteps();
      case 'universal_analysis':
        return getUniversalAnalysisSteps();
      case 'certificate_of_effort':
        return getCertificateOfEffortSteps();
      case 'coverage_analysis':
        return getCoverageAnalysisSteps();
      case 'rate_analysis':
        return getRateAnalysisSteps();
      case 'portfolio_analyzer':
        return getPortfolioAnalyzerSteps();
      case 'homeowners_comparison':
        return getHomeownersComparisonSteps();
      case 'personal_umbrella':
        return getPersonalUmbrellaSteps();
      case 'insurance_requirements':
        return getInsuranceRequirementsSteps();
      case 'financial_analysis':
        return getFinancialAnalysisSteps();
      case 'client_renewal_presentation':
        return getClientRenewalPresentationSteps();
      case 'cyber_auto':
      case 'cyber_benchmark':
      case 'cyber_renewal_delta':
      case 'cyber_war_systemic':
      case 'cyber_ransomware_trap':
      case 'cyber_quote_comparison':
      case 'do_auto':
      case 'do_benchmark':
      case 'do_renewal_delta':
      case 'do_distress_scanner':
      case 'do_emerging_exposure':
      case 'home_auto':
      case 'home_owner_benchmark':
      case 'home_tenant_condo_benchmark':
      case 'home_quote_comparison':
      case 'home_renewal_delta':
      case 'home_dp_rental_benchmark':
      case 'home_dp_rental_renewal_delta':
      case 'home_hnw_benchmark':
      case 'plines_auto':
      case 'pa_benchmark':
      case 'pa_quote_comparison':
      case 'pa_renewal_delta':
      case 'umb_benchmark':
      case 'umb_quote_comparison':
      case 'umb_renewal_delta':
      case 'rt_auto':
      case 'rt_requirements_reviewer':
      case 'rt_conformance_alignment':
      case 'rt_compliance_verifier':
      case 'premdev_auto':
      case 'premdev_workers_comp':
      case 'premdev_prof_liability':
      case 'premdev_general_liability':
      case 'premdev_property':
      case 'premdev_package_bop':
      case 'lossrun_auto':
      case 'lossrun_fact_engine':
      case 'lossrun_mitigation_wc':
      case 're_auto':
      case 're_policy_extraction':
      case 're_risk_transfer':
      case 're_br_financials':
      case 're_zone_extractor':
      case 're_portfolio_brief':
      case 'cl_auto':
      case 'cl_gl_benchmark':
      case 'cl_property_benchmark':
      case 'cl_commercial_auto_benchmark':
      case 'cl_inland_marine_benchmark':
      case 'cl_excess_benchmark':
      case 'cl_package_benchmark':
      case 'cl_bop_benchmark':
      case 'cl_nytpao_flow':
      case 'cl_package_quote_comparison':
      case 'cl_bop_quote_comparison':
      case 'cl_xarch_quote_comparison':
      case 'cl_package_renewal_delta':
      case 'cl_bop_renewal_delta':
      case 'ca_auto':
      case 'ca_coverage_determination':
      case 'ca_portfolio_determination':
      case 'ca_carrier_position_audit':
      case 'ntr_auto':
      case 'ntr_first_party':
      case 'ntr_property_liability':
      case 'ntr_mgmt_professional':
        // All BETA suites share the same doc-selection -> optional-context ->
        // run flow as the cyber suite that originated it — one generic
        // builder handles every suite (parameterized by the outer
        // `workflowType` prop). See REMAINING_BETA_SUITES_PLAN.md Phase 0.
        return getCyberSuiteSteps();
      default:
        return [];
    }
  };

  const getNewBusinessSteps = (): WorkflowStep[] => [
    {
      id: 'business_type',
      title: 'Select Business Type',
      component: <BusinessTypeSelection onSelect={handleBusinessTypeSelect} />,
      canProceed: !!workflowData.businessType
    },
    {
      id: 'tool_selection',
      title: 'Select Tool',
      component: <ToolSelection 
        businessType={workflowData.businessType}
        onSelect={handleToolSelect}
      />,
      canProceed: !!workflowData.selectedTool
    },
    {
      id: 'execute_tool',
      title: 'Execute Tool',
      component: <ToolExecution 
        tool={workflowData.selectedTool}
        clientId={clientId}
        projectId={projectId}
        onComplete={handleToolComplete}
        loading={loading}
      />,
      canProceed: !!workflowData.toolResult
    },
    {
      id: 'save_point_1',
      title: 'Save Progress',
      component: <SavePoint 
        onSave={() => handleSave('initial_analysis')}
        onContinue={() => setCurrentStep(currentStep + 1)}
        canSave={!!workflowData.toolResult}
      />,
      canProceed: true
    },
    {
      id: 'policy_comparison',
      title: 'Policy Comparison',
      component: <PolicyComparison 
        researchReport={workflowData.toolResult}
        clientId={clientId}
        projectId={projectId}
        onComplete={handlePolicyComparisonComplete}
        loading={loading}
      />,
      canProceed: !!workflowData.comparisonReport
    },
    {
      id: 'quote_comparison_tool',
      title: 'Quote Comparison Tool',
      component: <QuoteComparisonTool 
        context={workflowData}
        clientId={clientId}
        projectId={projectId}
      />,
      canProceed: true
    }
  ];

  const getRenewalSteps = (): WorkflowStep[] => [
    {
      id: 'document_source',
      title: 'Upload Documents',
      component: <DocumentSourceSelection 
        clientId={clientId}
        projectId={projectId}
        onSelect={handleRenewalDocumentSelect}
        workflowType="renewal"
      />,
      canProceed: !!workflowData.renewalDocuments
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep 
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific instructions or context for the renewal analysis..."
        customerId={clientId}
        projectId={projectId}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <RenewalAnalysis
        documents={workflowData.renewalDocuments}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        projectId={projectId}
        pastReports={[...(workflowData.pastReports || []), ...(workflowData.additionalPastReports || [])]}
        additionalPastDocuments={workflowData.additionalPastDocuments}
        onComplete={handleRenewalAnalysisComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'renewal_analysis', workflowData.renewalDocuments || [])}
        token={token}
        loading={loading}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.analysisReport
    }
  ];

  const getDocumentComparisonSteps = (): WorkflowStep[] => [
    {
      id: 'document_upload',
      title: 'Upload Documents',
      component: <ComparisonDocumentUpload 
        clientId={clientId}
        projectId={projectId}
        onDocumentsReady={handleComparisonDocumentsReady}
        comparisonType={workflowData.comparisonType}
      />,
      canProceed: !!workflowData.comparisonDocuments && (workflowData.comparisonDocuments.length + (workflowData.pastReports?.length || 0)) >= 2
    },
    {
      id: 'additional_context',
      title: 'Additional Context',
      component: <ComparisonAdditionalContext 
        onContextReady={handleComparisonContextReady}
        documents={workflowData.comparisonDocuments || []}
        clientId={clientId}
        projectId={projectId}
      />,
      canProceed: true // This step is optional, so always can proceed
    },
    {
      id: 'execute_comparison',
      title: 'Execute Comparison',
      component: <ComparisonExecution
        documents={workflowData.comparisonDocuments || []}
        additionalContext={workflowData.additionalContext || ''}
        comparisonType={workflowData.comparisonType || 'document_comparison'}
        additionalDocs={workflowData.additionalDocuments || []}
        clientId={clientId}
        projectId={projectId}
        pastReports={workflowData.pastReports || []}
        onExecutionComplete={handleDocumentComparisonComplete}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'document_comparison', workflowData.comparisonDocuments || [])}
        token={token}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.comparisonResult
    }
  ];

  const getLossRunAnalysisSteps = (): WorkflowStep[] => [
    {
      id: 'document_source',
      title: 'Select Document Source',
      component: <DocumentSourceSelection
        clientId={clientId}
        projectId={projectId}
        onSelect={handleLossRunDocumentSelect}
        workflowType="loss_run"
        initialPastDocumentIds={workflowData.initialSelectedDocumentIds}
        suggestionsLoading={catalogSuggestionsLoading}
      />,
      canProceed: !!workflowData.lossRunDocuments
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific instructions or context for the loss run analysis..."
        customerId={clientId}
        projectId={projectId}
        initialValue={workflowData.initialContext}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <LossRunAnalysis
        documents={workflowData.lossRunDocuments}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        projectId={projectId}
        pastReports={[...(workflowData.pastReports || []), ...(workflowData.additionalPastReports || [])]}
        additionalPastDocuments={workflowData.additionalPastDocuments}
        onComplete={handleLossRunAnalysisComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'loss_run_analysis', workflowData.lossRunDocuments || [])}
        token={token}
        loading={loading}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.analysisReport
    },
    {
      id: 'analysis_chat',
      title: 'Analysis Chat',
      component: <ChatInterface
        messages={[]}
        onSendMessage={() => {}}
        documents={workflowData.lossRunDocuments}
      />,
      canProceed: true
    }
  ];

  const getYearOverYearSteps = (): WorkflowStep[] => [
    {
      id: 'document_source',
      title: 'Upload Documents',
      component: <DocumentSourceSelection
        clientId={clientId}
        projectId={projectId}
        onSelect={handleYearOverYearDocumentSelect}
        workflowType="year_over_year"
        initialPastDocumentIds={workflowData.initialSelectedDocumentIds}
        suggestionsLoading={catalogSuggestionsLoading}
      />,
      canProceed: !!workflowData.yearOverYearDocuments
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific instructions or context for the year-over-year analysis..."
        customerId={clientId}
        projectId={projectId}
        initialValue={workflowData.initialContext}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <YearOverYearAnalysis
        documents={workflowData.yearOverYearDocuments}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        projectId={projectId}
        pastReports={[...(workflowData.pastReports || []), ...(workflowData.additionalPastReports || [])]}
        additionalPastDocuments={workflowData.additionalPastDocuments}
        onComplete={handleYearOverYearAnalysisComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'year_over_year', workflowData.yearOverYearDocuments || [])}
        token={token}
        loading={loading}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.yearOverYearResult
    }
  ];

  const getYoyRenewalSteps = (): WorkflowStep[] => [
    {
      id: 'document_source',
      title: 'Upload Documents',
      component: <DocumentSourceSelection
        clientId={clientId}
        projectId={projectId}
        onSelect={handleYoyRenewalDocumentSelect}
        workflowType="yoy_renewal"
        initialPastDocumentIds={workflowData.initialSelectedDocumentIds}
        suggestionsLoading={catalogSuggestionsLoading}
      />,
      canProceed: !!workflowData.yoyRenewalDocuments
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific instructions or context for the year-over-year renewal analysis..."
        customerId={clientId}
        projectId={projectId}
        initialValue={workflowData.initialContext}
      />,
      canProceed: true
    },
    {
      id: 'report_options',
      title: 'Report Options',
      component: <ReportOptionsStep
        value={workflowData.reportOptions}
        onChange={(opts) => setWorkflowData((prev: any) => ({ ...prev, reportOptions: opts }))}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <YearOverYearAnalysis
        documents={workflowData.yoyRenewalDocuments}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        projectId={projectId}
        pastReports={[...(workflowData.pastReports || []), ...(workflowData.additionalPastReports || [])]}
        additionalPastDocuments={workflowData.additionalPastDocuments}
        onComplete={handleYoyRenewalAnalysisComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'yoy_renewal', workflowData.yoyRenewalDocuments || [])}
        token={token}
        loading={loading}
        workflowType="yoy_renewal"
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
        reportOptions={workflowData.reportOptions}
      />,
      canProceed: !!workflowData.yoyRenewalResult
    }
  ];

  const getPersonalLinesYoyRenewalSteps = (): WorkflowStep[] => [
    {
      id: 'document_source',
      title: 'Upload Documents',
      component: <DocumentSourceSelection
        clientId={clientId}
        projectId={projectId}
        onSelect={handlePersonalLinesYoyRenewalDocumentSelect}
        workflowType="personal_lines_yoy_renewal"
        initialPastDocumentIds={workflowData.initialSelectedDocumentIds}
        suggestionsLoading={catalogSuggestionsLoading}
      />,
      canProceed: !!workflowData.personalLinesYoyRenewalDocuments
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific instructions or context for the personal lines year-over-year renewal analysis (homeowners, auto, umbrella, specialty)..."
        customerId={clientId}
        projectId={projectId}
        initialValue={workflowData.initialContext}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <YearOverYearAnalysis
        documents={workflowData.personalLinesYoyRenewalDocuments}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        projectId={projectId}
        pastReports={[...(workflowData.pastReports || []), ...(workflowData.additionalPastReports || [])]}
        additionalPastDocuments={workflowData.additionalPastDocuments}
        onComplete={handlePersonalLinesYoyRenewalAnalysisComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'personal_lines_yoy_renewal', workflowData.personalLinesYoyRenewalDocuments || [])}
        token={token}
        loading={loading}
        workflowType="personal_lines_yoy_renewal"
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.personalLinesYoyRenewalResult
    }
  ];

  const getCommercialLinesExecRenewalSteps = (): WorkflowStep[] => [
    {
      id: 'document_source',
      title: 'Upload Documents',
      component: <DocumentSourceSelection
        clientId={clientId}
        projectId={projectId}
        onSelect={handleCommercialLinesExecRenewalDocumentSelect}
        workflowType="commercial_lines_exec_renewal"
      />,
      canProceed: !!workflowData.commercialLinesExecRenewalDocuments
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific instructions, company website URL, or context for the commercial lines executive renewal analysis..."
        customerId={clientId}
        projectId={projectId}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <YearOverYearAnalysis
        documents={workflowData.commercialLinesExecRenewalDocuments}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        projectId={projectId}
        pastReports={[...(workflowData.pastReports || []), ...(workflowData.additionalPastReports || [])]}
        additionalPastDocuments={workflowData.additionalPastDocuments}
        onComplete={handleCommercialLinesExecRenewalAnalysisComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'commercial_lines_exec_renewal', workflowData.commercialLinesExecRenewalDocuments || [])}
        token={token}
        loading={loading}
        workflowType="commercial_lines_exec_renewal"
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.commercialLinesExecRenewalResult
    }
  ];

  const getInsuranceRequirementsSteps = (): WorkflowStep[] => [
    {
      id: 'document_source',
      title: 'Upload Documents',
      component: <DocumentSourceSelection
        clientId={clientId}
        projectId={projectId}
        onSelect={handleInsuranceRequirementsDocumentSelect}
        workflowType="insurance_requirements"
        initialPastDocumentIds={workflowData.initialSelectedDocumentIds}
        suggestionsLoading={catalogSuggestionsLoading}
      />,
      canProceed: !!workflowData.insuranceRequirementsDocuments
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific instructions or context for the insurance requirements analysis..."
        customerId={clientId}
        projectId={projectId}
        initialValue={workflowData.initialContext}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <InsuranceRequirementsAnalysis
        documents={workflowData.insuranceRequirementsDocuments}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        projectId={projectId}
        onComplete={handleInsuranceRequirementsAnalysisComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'insurance_requirements', workflowData.insuranceRequirementsDocuments || [])}
        token={token}
        loading={loading}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.insuranceRequirementsResult
    }
  ];

  const getFinancialAnalysisSteps = (): WorkflowStep[] => [
    {
      id: 'document_source',
      title: 'Upload Financial Documents',
      component: <DocumentSourceSelection
        clientId={clientId}
        projectId={projectId}
        onSelect={handleFinancialAnalysisDocumentSelect}
        workflowType="financial_analysis"
      />,
      canProceed: !!workflowData.financialAnalysisDocuments
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific instructions or context for the financial analysis (e.g., specific risk concerns, industry focus)..."
        customerId={clientId}
        projectId={projectId}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <FinancialAnalysis
        documents={workflowData.financialAnalysisDocuments}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        projectId={projectId}
        onComplete={handleFinancialAnalysisComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'financial_analysis', workflowData.financialAnalysisDocuments || [])}
        token={token}
        loading={loading}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.financialAnalysisResult
    }
  ];

  const getClientRenewalPresentationSteps = (): WorkflowStep[] => [
    {
      id: 'document_source',
      title: 'Upload Insurance Documents',
      component: <DocumentSourceSelection
        clientId={clientId}
        projectId={projectId}
        onSelect={handleClientRenewalPresentationDocumentSelect}
        workflowType="client_renewal_presentation"
      />,
      canProceed: !!workflowData.clientRenewalPresentationDocuments
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific instructions or context for the client renewal presentation (e.g., particular coverage concerns, client preferences)..."
        customerId={clientId}
        projectId={projectId}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Generate Presentation',
      component: <ClientRenewalPresentation
        documents={workflowData.clientRenewalPresentationDocuments}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        projectId={projectId}
        onComplete={handleClientRenewalPresentationComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'client_renewal_presentation', workflowData.clientRenewalPresentationDocuments || [])}
        token={token}
        loading={loading}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.clientRenewalPresentationResult
    }
  ];

  const getResearchToolSteps = (): WorkflowStep[] => [
    {
      id: 'url_input',
      title: 'Enter Company Details',
      component: <ResearchUrlInput
        onUrlSet={handleResearchUrlSet}
        clientId={clientId}
        projectId={projectId}
        clientName={clientName}
      />,
      canProceed: !!(workflowData.companyName || workflowData.companyUrl || workflowData.linkedinUrl)
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific instructions or context for the company research..."
        customerId={clientId}
        projectId={projectId}
      />,
      canProceed: true
    },
    {
      id: 'run_research',
      title: 'Run Research',
      component: <ResearchExecution
        companyUrl={workflowData.companyUrl}
        linkedinUrl={workflowData.linkedinUrl}
        companyName={workflowData.companyName}
        additionalContext={workflowData.additionalContext}
        documents={workflowData.researchDocuments}
        additionalPastDocuments={workflowData.additionalPastDocuments}
        pastReports={workflowData.additionalPastReports}
        onComplete={handleResearchComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'research', workflowData.researchDocuments || [])}
        token={token}
        loading={loading}
        clientId={clientId}
        projectId={projectId}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!(workflowData.companyName || workflowData.companyUrl || workflowData.linkedinUrl)
    }
  ];

  // Event handlers
  const handleResearchUrlSet = (url: string, additionalContext?: string, documents?: any[], linkedinUrl?: string, companyName?: string) => {
    setWorkflowData((prev: any) => ({
      ...prev,
      companyUrl: url,
      linkedinUrl: linkedinUrl || '',
      companyName: companyName || '',
      additionalContext: additionalContext || '',
      researchDocuments: documents || []
    }));
    // Don't auto-advance - let user click Next button when ready
  };

  const handleBusinessTypeSelect = (businessType: 'existing_business' | 'new_business') => {
    setWorkflowData((prev: any) => ({ ...prev, businessType }));
    if (businessType === 'new_business') {
      // Route to separate New Business flow (to be defined)
      setError('New Business flow not yet implemented');
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const handleToolSelect = (tool: 'research_tool' | 'loss_run_tool') => {
    if (tool === 'research_tool' && onWorkflowSwitch) {
      // Switch to dedicated Research Tool workflow
      onWorkflowSwitch('research_tool');
      return;
    }
    
    setWorkflowData((prev: any) => ({ ...prev, selectedTool: tool }));
    setCurrentStep(currentStep + 1);
  };

  const handleToolComplete = async (result: any) => {
    devLog('🔍 Tool result received:', {
      hasContent: !!result?.content,
      contentLength: result?.content?.length || 0,
      resultType: typeof result,
      resultKeys: result ? Object.keys(result) : [],
      contentPreview: result?.content?.substring(0, 100) + '...'
    });
    
    setWorkflowData((prev: any) => ({ ...prev, toolResult: result }));
    
    // Report is ready - close the modal. User can view it via the notification bell.
    onClose();
  };

  // Handler for when a child component successfully queues a job
  const handleJobQueued = (jobId: string, workflowTypeUsed: string, documentsUsed: any[]) => {
    devLog(`✅ Job queued from child component: ${jobId}`);

    // Add to background reports for tracking
    addBackgroundReport({
      workflowId: jobId,
      workflowType: workflowTypeUsed,
      customerName: clientName,
      customerId: clientId,
      projectId: projectId,
      documents: documentsUsed?.map((d: any) => ({ id: d.id, name: d.name || d.fileName }))
    });

    // Refresh reports to pick up the new job
    refreshReports();

    // Show success toast
    showToast({
      type: 'info',
      title: 'Workflow Queued',
      message: `${formatWorkflowTitle(workflowTypeUsed)} for ${clientName} is processing. You'll be notified when it's ready.`,
      duration: 5000,
    });

    // Close modal
    setLoading(false);
    onClose();
  };

  // Handler for when child components start long-running executions
  // This enables the 3-minute background timer for workflows that run in child components
  const handleChildExecutionStart = async (workflowInfo: { workflowType: string; documents: any[] }) => {
    devLog('🚀 Child component started execution:', workflowInfo);
    setExecutionStartTime(new Date());
    setShowBackgroundOption(false);

    // Generate a temporary workflow ID for tracking
    const tempWorkflowId = `workflow_${workflowInfo.workflowType}_${Date.now()}`;
    setCurrentWorkflowId(tempWorkflowId);

    // Store workflow info for potential background continuation
    setWorkflowData((prev: any) => ({
      ...prev,
      currentExecutingWorkflow: {
        ...workflowInfo,
        tempWorkflowId,
        startedAt: new Date().toISOString()
      }
    }));

    // Check if there's already an active background report - auto-background this one
    if (hasActiveBackgroundReport) {
      devLog('🔄 Active background report detected - auto-queuing new workflow to background');

      // Mark workflow as background on the server
      const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
      const token = localStorage.getItem('riskninja-token');

      try {
        const backgroundResponse = await fetch(`${API_BASE_URL}/api/workflows/${tempWorkflowId}/background`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            customerId: clientId,
            workflowType: workflowInfo.workflowType
          })
        });

        if (backgroundResponse.ok) {
          devLog('✅ Workflow marked as background on server');
        }
      } catch (err) {
        console.log('Note: Could not mark workflow as background on server:', err);
      }

      // Add to background reports context
      addBackgroundReport({
        workflowId: tempWorkflowId,
        workflowType: workflowInfo.workflowType,
        customerName: clientName,
        customerId: clientId,
        projectId: projectId,
        documents: workflowInfo.documents?.map((d: any) => ({ id: d.id, name: d.name || d.fileName }))
      });

      // Show toast notification
      showToast({
        type: 'info',
        title: 'Workflow Queued',
        message: 'Workflow added to background queue. You\'ll be notified when it\'s ready.',
        duration: 4000
      });

      // Close the modal and return to workflow selection
      setLoading(false);
      onClose();
      return;
    }

    // Start the 3-minute timer to show background option
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
    }

    backgroundTimerRef.current = setTimeout(() => {
      devLog('⏰ 3-minute timer expired for child workflow - showing background option');
      setShowBackgroundOption(true);
    }, BACKGROUND_TIMER_MS);
  };

  const handleSave = async (stage: string) => {
    try {
      setLoading(true);
      await saveWorkflow(stage, workflowData);
    } catch (error) {
      setError('Failed to save workflow');
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyComparisonComplete = (comparisonReport: any) => {
    setWorkflowData((prev: any) => ({ ...prev, comparisonReport }));
    
    // Navigate directly to full report page for policy comparison
    const reportData = {
      content: comparisonReport.content || comparisonReport,
      workflowType: workflowType,
      documents: workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: comparisonReport.citations || [],
      projectId: projectId
    };
    
    devLog('📊 Auto-navigating to policy comparison report:', reportData);
    
    // Store data using the safe route data hook
    storeData(reportData);
    
    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  const handleApplicationUpload = async (files: File[], context?: string) => {
    try {
      setLoading(true);
      devLog(`📤 Uploading ${files.length} application files for customer ${clientId}`);
      
      const uploadedDocs = await uploadFilesWithCustomer(files);
      
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        applications: uploadedDocs, // Store uploaded document objects instead of File objects
        additionalContext: context 
      }));
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Error uploading application files:', error);
      setError('Failed to upload application files');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to upload files with customer association
  const uploadFilesWithCustomer = async (files: File[]): Promise<any[]> => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.avery.cloud';
    const uploadedDocs = [];
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('document', file);
        formData.append('name', file.name);
        formData.append('customerId', clientId); // Associate with selected customer
        
        const response = await fetch(`${API_BASE_URL}/api/documents`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
          },
          body: formData
        });
        
        if (response.ok) {
          const uploadedDoc = await response.json();
          devLog(`✅ Uploaded ${file.name} for customer ${clientId}:`, uploadedDoc);
          uploadedDocs.push(uploadedDoc);
        } else {
          console.error(`❌ Failed to upload ${file.name}:`, response.status);
          throw new Error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        throw error;
      }
    }
    
    return uploadedDocs;
  };

  const handlePreviousApplicationUpload = async (files: File[]) => {
    try {
      setLoading(true);
      devLog(`📤 Uploading ${files.length} previous application files for customer ${clientId}`);
      
      const uploadedDocs = await uploadFilesWithCustomer(files);
      
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        previousApplications: uploadedDocs // Store uploaded document objects instead of File objects
      }));
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Error uploading previous application files:', error);
      setError('Failed to upload previous application files');
    } finally {
      setLoading(false);
    }
  };

  const handleNewApplicationUpload = async (files: File[], context?: string) => {
    try {
      setLoading(true);
      devLog(`📤 Uploading ${files.length} new application files for customer ${clientId}`);
      
      const uploadedDocs = await uploadFilesWithCustomer(files);
      
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        newApplications: uploadedDocs, // Store uploaded document objects instead of File objects
        additionalContext: context 
      }));
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Error uploading new application files:', error);
      setError('Failed to upload new application files');
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationComparisonComplete = (comparisonReport: any) => {
    setWorkflowData((prev: any) => ({ ...prev, comparisonReport }));
    
    // Navigate directly to full report page for application comparison
    const reportData = {
      content: comparisonReport.content || comparisonReport,
      workflowType: workflowType,
      documents: workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: comparisonReport.citations || [],
      projectId: projectId
    };
    
    devLog('📊 Auto-navigating to application comparison report:', reportData);
    
    // Store data using the safe route data hook
    storeData(reportData);
    
    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  const handleResearchComplete = (researchReport: any) => {
    setWorkflowData((prev: any) => ({ ...prev, researchReport }));
    
    devLog('🔍 Research report received from backend:', {
      hasContent: !!researchReport?.content,
      contentType: typeof researchReport?.content,
      topLevelKeys: Object.keys(researchReport || {}),
      isContentJSON: researchReport?.content && typeof researchReport.content === 'object'
    });
    
    // The backend returns: { id, content: jsonResponse, title, workflowType, ... }
    // Where content is ALREADY a parsed UniversalJsonResponse object!
    const finalContent = researchReport?.content;
    
    devLog('🔍 Research content structure:', {
      contentType: typeof finalContent,
      hasResponseMetadata: !!(finalContent?.responseMetadata),
      hasExecutiveSummary: !!(finalContent?.executiveSummary),
      hasAnalysisMatrices: !!(finalContent?.analysisMatrices),
      hasKeyFindings: !!(finalContent?.keyFindings),
      contentKeys: finalContent ? Object.keys(finalContent) : []
    });
    
    // Since the backend sends a UniversalJsonResponse as content, flag it as Universal JSON
    const isUniversalJson = finalContent && 
                           typeof finalContent === 'object' && 
                           finalContent.responseMetadata && 
                           finalContent.executiveSummary && 
                           finalContent.analysisMatrices && 
                           finalContent.keyFindings;
    
    devLog('✅ Research workflow:', isUniversalJson ? 'Has Universal JSON structure' : 'Missing Universal JSON structure');
    
    // Navigate using EXACT same pattern as renewal workflow that WORKS
    const reportData = {
      content: finalContent, // Pass the UniversalJsonResponse object as content
      workflowType: 'research_tool',
      documents: [], // Research has no documents
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: [], // No citations for research
      projectId: projectId,
      // Mark as Universal JSON if it has the structure
      isUniversalJson: isUniversalJson,
      universalJsonResponse: isUniversalJson ? finalContent : null
    };
    
    devLog('📊 Auto-navigating to research report with clean data:', {
      hasContent: !!reportData.content,
      contentType: typeof reportData.content,
      isUniversalJson: reportData.isUniversalJson,
      workflowType: reportData.workflowType
    });
    
    // Store data using the safe route data hook
    storeData(reportData);
    devLog('✅ Stored report data using safe route data hook');
    
    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // Renewal workflow handlers - following exact loss run pattern
  const handleRenewalDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    devLog('📄 Selected renewal documents:', documents);
    
    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      // Legacy format - just an array of documents
      setWorkflowData((prev: any) => ({ ...prev, renewalDocuments: documents }));
    } else {
      // New enhanced format with past documents and reports
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        renewalDocuments: documents.allDocuments, // For backward compatibility
        pastDocuments: documents.pastDocuments,
        pastReports: documents.pastReports,
        renewalData: documents // Store the full enhanced data
      }));
    }
    // Don't auto-advance - let user click Next button when ready
  };

  const handleContextSubmit = (context: string, pastDocuments?: any[], pastReports?: any[], additionalFiles?: any[]) => {
    // Merge newly uploaded files into additionalPastDocuments so downstream components
    // (e.g. ResearchExecution) receive them via a single prop. Both shapes are objects
    // with {id, name, ...} and get collapsed to IDs at submit time.
    const mergedAdditionalDocs = [
      ...(pastDocuments || []),
      ...(additionalFiles || []),
    ];
    devLog('📝 Additional context submitted:', context);
    devLog('📄 Past documents selected:', pastDocuments?.length || 0);
    devLog('📋 Past reports selected:', pastReports?.length || 0);
    devLog('📎 Additional files uploaded:', additionalFiles?.length || 0);
    setWorkflowData((prev: any) => ({
      ...prev,
      additionalContext: context,
      additionalPastDocuments: mergedAdditionalDocs,
      additionalPastReports: pastReports || [],
      additionalFiles: additionalFiles || []
    }));
  };

  // Handler for workflow analysis errors - propagates to modal error banner
  const handleWorkflowError = useCallback((errorMessage: string) => {
    console.error('❌ Workflow error:', errorMessage);
    // Clear the background timer since execution failed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);
    setError(errorMessage);
    setLoading(false);
  }, []);

  const handleRenewalAnalysisComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, analysisReport }));

    // Navigate directly to full report page for renewal analysis - EXACT same pattern as loss run
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: workflowType,
      documents: workflowData.renewalDocuments || workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: analysisReport.citations || [],
      projectId: projectId
    };

    // Check if modal is closed (user moved on) or workflow was explicitly backgrounded
    // In either case, show toast instead of auto-navigating
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      // Store report data for later viewing
      storeData(reportData);

      // Remove the local background report since it's now complete
      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      // Show toast notification with action to view report
      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Renewal Analysis for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });
      return;
    }

    devLog('📊 Auto-navigating to renewal analysis report:', reportData);

    // CRITICAL FIX: Store data using the safe route data hook
    storeData(reportData);
    devLog('✅ Stored report data using safe route data hook');

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // Year-over-year workflow handlers - following EXACT same pattern as renewal
  const handleYearOverYearDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    devLog('📄 Selected year-over-year documents:', documents);
    
    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      // Legacy format - just an array of documents
      setWorkflowData((prev: any) => ({ ...prev, yearOverYearDocuments: documents }));
    } else {
      // New enhanced format with past documents and reports
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        yearOverYearDocuments: documents.allDocuments, // For backward compatibility
        pastDocuments: documents.pastDocuments,
        pastReports: documents.pastReports,
        yearOverYearData: documents // Store the full enhanced data
      }));
    }
    // Don't auto-advance - let user click Next button when ready
  };

  const handleYearOverYearAnalysisComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, yearOverYearResult: analysisReport }));

    // Navigate directly to full report page for year-over-year analysis - EXACT same pattern as renewal
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: workflowType,
      documents: workflowData.yearOverYearDocuments || workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: analysisReport.citations || [],
      projectId: projectId
    };

    // Check if modal is closed (user moved on) or workflow was explicitly backgrounded
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      // Store report data for later viewing
      storeData(reportData);

      // Remove the local background report since it's now complete
      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      // Show toast notification with action to view report
      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Year Over Year Analysis for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });
      return;
    }

    devLog('📊 Auto-navigating to year-over-year analysis report:', reportData);

    // Store data using the safe route data hook
    storeData(reportData);

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  const handleYearOverYearDocumentUpload = async (files: File[], context?: string) => {
    try {
      setLoading(true);
      devLog(`📤 Uploading ${files.length} year-over-year documents for customer ${clientId}`);
      
      const uploadedDocs = await uploadFilesWithCustomer(files);
      
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        yearOverYearDocuments: uploadedDocs, // Store uploaded document objects instead of File objects
        additionalContext: context 
      }));
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Error uploading year-over-year documents:', error);
      setError('Failed to upload year-over-year documents');
    } finally {
      setLoading(false);
    }
  };

  // YOY Renewal workflow handlers - following same pattern as year_over_year
  const handleYoyRenewalDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    devLog('📄 Selected YOY renewal documents:', documents);

    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      // Legacy format - just an array of documents
      setWorkflowData((prev: any) => ({ ...prev, yoyRenewalDocuments: documents }));
    } else {
      // New enhanced format with past documents and reports
      setWorkflowData((prev: any) => ({
        ...prev,
        yoyRenewalDocuments: documents.allDocuments, // For backward compatibility
        pastDocuments: documents.pastDocuments,
        pastReports: documents.pastReports,
        yoyRenewalData: documents // Store the full enhanced data
      }));
    }
    // Don't auto-advance - let user click Next button when ready
  };

  const handleYoyRenewalAnalysisComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, yoyRenewalResult: analysisReport }));

    // Navigate directly to full report page for YOY renewal analysis
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: 'yoy_renewal',
      documents: workflowData.yoyRenewalDocuments || workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: analysisReport.citations || [],
      projectId: projectId
    };

    // Check if modal is closed (user moved on) or workflow was explicitly backgrounded
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      storeData(reportData);

      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `YOY Renewal Analysis for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });
      return;
    }

    devLog('📊 Auto-navigating to YOY renewal analysis report:', reportData);

    storeData(reportData);

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // Personal Lines YOY Renewal workflow handlers - following same pattern as yoy_renewal
  const handlePersonalLinesYoyRenewalDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    devLog('📄 Selected Personal Lines YOY renewal documents:', documents);

    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      // Legacy format - just an array of documents
      setWorkflowData((prev: any) => ({ ...prev, personalLinesYoyRenewalDocuments: documents }));
    } else {
      // New enhanced format with past documents and reports
      setWorkflowData((prev: any) => ({
        ...prev,
        personalLinesYoyRenewalDocuments: documents.allDocuments, // For backward compatibility
        pastDocuments: documents.pastDocuments,
        pastReports: documents.pastReports,
        personalLinesYoyRenewalData: documents // Store the full enhanced data
      }));
    }
    // Don't auto-advance - let user click Next button when ready
  };

  const handlePersonalLinesYoyRenewalAnalysisComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, personalLinesYoyRenewalResult: analysisReport }));

    // Navigate directly to full report page for Personal Lines YOY renewal analysis
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: 'personal_lines_yoy_renewal',
      documents: workflowData.personalLinesYoyRenewalDocuments || workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: analysisReport.citations || [],
      projectId: projectId
    };

    // Check if modal is closed OR this workflow was sent to background - show toast instead of auto-navigating
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      // Store report data for later viewing
      storeData(reportData);

      // Remove the local background report since it's now complete
      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      // Show toast notification with action to view report
      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Personal Lines YOY Renewal for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });

      return;
    }

    devLog('📊 Auto-navigating to Personal Lines YOY renewal analysis report:', reportData);

    // Store data using the safe route data hook
    storeData(reportData);

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // Commercial Lines Executive Renewal workflow handlers - following same pattern as personal lines
  const handleCommercialLinesExecRenewalDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    devLog('📄 Selected Commercial Lines Executive Renewal documents:', documents);

    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      // Legacy format - just an array of documents
      setWorkflowData((prev: any) => ({ ...prev, commercialLinesExecRenewalDocuments: documents }));
    } else {
      // New enhanced format with past documents and reports
      setWorkflowData((prev: any) => ({
        ...prev,
        commercialLinesExecRenewalDocuments: documents.allDocuments, // For backward compatibility
        pastDocuments: documents.pastDocuments,
        pastReports: documents.pastReports,
        commercialLinesExecRenewalData: documents // Store the full enhanced data
      }));
    }
    // Don't auto-advance - let user click Next button when ready
  };

  const handleCommercialLinesExecRenewalAnalysisComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, commercialLinesExecRenewalResult: analysisReport }));

    // Navigate directly to full report page for Commercial Lines Executive Renewal analysis
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: 'commercial_lines_exec_renewal',
      documents: workflowData.commercialLinesExecRenewalDocuments || workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: analysisReport.citations || [],
      projectId: projectId
    };

    // Check if modal is closed OR this workflow was sent to background - show toast instead of auto-navigating
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      // Store report data for later viewing
      storeData(reportData);

      // Remove the local background report since it's now complete
      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      // Show toast notification with action to view report
      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Commercial Lines Exec Renewal for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });

      return;
    }

    devLog('📊 Auto-navigating to Commercial Lines Executive Renewal analysis report:', reportData);

    // Store data using the safe route data hook
    storeData(reportData);

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // Insurance Requirements workflow handlers - following same pattern as year-over-year
  const handleInsuranceRequirementsDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    devLog('📄 Selected insurance requirements documents:', documents);

    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      // Legacy format - just an array of documents
      setWorkflowData((prev: any) => ({ ...prev, insuranceRequirementsDocuments: documents }));
    } else {
      // New enhanced format with past documents and reports
      setWorkflowData((prev: any) => ({
        ...prev,
        insuranceRequirementsDocuments: documents.allDocuments, // For backward compatibility
        pastDocuments: documents.pastDocuments,
        pastReports: documents.pastReports,
        insuranceRequirementsData: documents // Store the full enhanced data
      }));
    }
    // Don't auto-advance - let user click Next button when ready
  };

  const handleInsuranceRequirementsAnalysisComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, insuranceRequirementsResult: analysisReport }));

    // Navigate directly to full report page for insurance requirements analysis
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: workflowType,
      documents: workflowData.insuranceRequirementsDocuments || workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: analysisReport.citations || [],
      projectId: projectId
    };

    // Check if modal is closed OR this workflow was sent to background - show toast instead of auto-navigating
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      // Store report data for later viewing
      storeData(reportData);

      // Remove the local background report since it's now complete
      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      // Show toast notification with action to view report
      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Insurance Requirements Analysis for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });

      return;
    }

    devLog('📊 Auto-navigating to insurance requirements analysis report:', reportData);

    // Store data using the safe route data hook
    storeData(reportData);

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  const handleFinancialAnalysisDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    devLog('📄 Selected financial analysis documents:', documents);

    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      // Legacy format - just an array of documents
      setWorkflowData((prev: any) => ({ ...prev, financialAnalysisDocuments: documents }));
    } else {
      // New enhanced format with past documents and reports
      setWorkflowData((prev: any) => ({
        ...prev,
        financialAnalysisDocuments: documents.allDocuments,
        pastDocuments: documents.pastDocuments,
        pastReports: documents.pastReports,
        financialAnalysisData: documents
      }));
    }
    // Don't auto-advance - let user click Next button when ready
  };

  const handleFinancialAnalysisComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, financialAnalysisResult: analysisReport }));

    // Navigate directly to full report page for financial analysis
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: 'financial_analysis',
      documents: workflowData.financialAnalysisDocuments || workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: analysisReport.citations || [],
      projectId: projectId
    };

    // Check if modal is closed OR this workflow was sent to background - show toast instead of auto-navigating
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      // Store report data for later viewing
      storeData(reportData);

      // Remove the local background report since it's now complete
      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      // Show toast notification with action to view report
      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Financial Analysis for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });

      return;
    }

    devLog('📊 Auto-navigating to financial analysis report:', reportData);

    // Store data using the safe route data hook
    storeData(reportData);

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  const handleClientRenewalPresentationDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    devLog('📄 Selected client renewal presentation documents:', documents);

    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      // Legacy format - just an array of documents
      setWorkflowData((prev: any) => ({ ...prev, clientRenewalPresentationDocuments: documents }));
    } else {
      // New enhanced format with past documents and reports
      setWorkflowData((prev: any) => ({
        ...prev,
        clientRenewalPresentationDocuments: documents.allDocuments,
        pastDocuments: documents.pastDocuments,
        pastReports: documents.pastReports,
        clientRenewalPresentationData: documents
      }));
    }
    // Don't auto-advance - let user click Next button when ready
  };

  const handleClientRenewalPresentationComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, clientRenewalPresentationResult: analysisReport }));

    // Navigate directly to full report page for client renewal presentation
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: 'client_renewal_presentation',
      documents: workflowData.clientRenewalPresentationDocuments || workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: analysisReport.citations || [],
      projectId: projectId
    };

    // Check if modal is closed OR this workflow was sent to background - show toast instead of auto-navigating
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      // Store report data for later viewing
      storeData(reportData);

      // Remove the local background report since it's now complete
      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      // Show toast notification with action to view report
      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Client Renewal Presentation for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });

      return;
    }

    devLog('📊 Auto-navigating to client renewal presentation report:', reportData);

    // Store data using the safe route data hook
    storeData(reportData);

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  const handleComparisonTypeSelect = (comparisonType: string) => {
    console.log('Setting comparison type:', comparisonType);
    setWorkflowData((prev: any) => ({ ...prev, comparisonType }));
    setForceUpdate(prev => prev + 1); // Force re-render of steps
    // Don't auto-advance - let user click Next button when ready
  };

  const handleDocumentSelect = (documents: any[]) => {
    setWorkflowData((prev: any) => ({ ...prev, selectedDocuments: documents }));
    // Don't auto-advance - let user click Next button when ready
  };

  const handleQuoteBinderSelect = (documents: any[]) => {
    console.log('handleQuoteBinderSelect called with:', documents);
    if (documents.length > 0) {
      console.log('Setting quoteBinder document:', documents[0]);
      setWorkflowData((prev: any) => ({ ...prev, quoteBinder: documents[0] }));
      // Don't auto-advance - let user click Next button when ready
    } else {
      console.log('No documents provided to handleQuoteBinderSelect');
    }
  };

  const handlePolicySelect = (documents: any[]) => {
    console.log('handlePolicySelect called with:', documents);
    if (documents.length > 0) {
      console.log('Setting policy document:', documents[0]);
      setWorkflowData((prev: any) => ({ ...prev, policy: documents[0] }));
      // Don't auto-advance - let user click Next button when ready
    } else {
      console.log('No documents provided to handlePolicySelect');
    }
  };

  const handleBinderSelect = (documents: any[]) => {
    console.log('handleBinderSelect called with:', documents);
    if (documents.length > 0) {
      console.log('Setting binder document:', documents[0]);
      setWorkflowData((prev: any) => ({ ...prev, binder: documents[0] }));
      console.log('Moving to next step from binder selection');
      setCurrentStep(currentStep + 1);
    } else {
      console.log('No documents provided to handleBinderSelect');
    }
  };

  const handleQuoteSelect = (documents: any[]) => {
    console.log('handleQuoteSelect called with:', documents);
    if (documents.length > 0) {
      console.log('Setting quote document:', documents[0]);
      setWorkflowData((prev: any) => ({ ...prev, quote: documents[0] }));
      console.log('Moving to next step from quote selection');
      setCurrentStep(currentStep + 1);
    } else {
      console.log('No documents provided to handleQuoteSelect');
    }
  };

  // New handler functions for the 3-step comparison wizard
  const handleComparisonDocumentsReady = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      // Legacy format - just an array of documents
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        comparisonDocuments: documents 
      }));
    } else {
      // New enhanced format with past documents and reports
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        comparisonDocuments: documents.allDocuments, // For backward compatibility
        comparisonData: documents, // Store the full enhanced data
        pastReports: documents.pastReports // Store past reports separately for easy access
      }));
    }
  };

  const handleComparisonContextReady = (context: string, comparisonType: string, additionalDocs: any[], pastDocuments?: any[], pastReports?: any[]) => {
    setWorkflowData((prev: any) => ({ 
      ...prev, 
      additionalContext: context,
      comparisonType: comparisonType,
      additionalDocuments: additionalDocs,
      additionalPastDocuments: pastDocuments || [],
      additionalPastReports: pastReports || []
    }));
  };

  const handleDocumentComparisonComplete = (comparisonResult: any) => {
    devLog('🔍 Processing comparison result:', {
      hasReport: !!comparisonResult.report,
      hasContent: !!comparisonResult.content,
      hasMetadata: !!comparisonResult.metadata,
      citationsCount: comparisonResult.citations?.length || 0,
      resultType: typeof comparisonResult,
      resultKeys: Object.keys(comparisonResult || {})
    });
    
    // Update workflow data first to trigger step progress
    setWorkflowData((prev: any) => ({ ...prev, comparisonResult }));
    
    // Navigate directly to full report page with enhanced format
    const reportData = {
      // Extract the actual markdown content from the enhanced response
      content: comparisonResult.report || comparisonResult.content || comparisonResult, // Handle new enhanced format first
      workflowType: comparisonResult.metadata?.comparisonType || workflowData.comparisonType || workflowType,
      documents: comparisonResult.metadata?.documentsCompared || workflowData.comparisonDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: comparisonResult.citations || [],
      metadata: comparisonResult.metadata || {
        generatedAt: new Date().toISOString(),
        processingTime: 0
      },
      projectId: projectId
    };
    
    devLog('📊 BEFORE navigation - modal state:', {
      isOpen,
      currentStep,
      hasComparisonResult: !!workflowData.comparisonResult,
      reportDataKeys: Object.keys(reportData)
    });
    
    // Close modal first, then navigate to avoid any modal interference
    onClose();
    
    // Use setTimeout to ensure modal closes before navigation
    setTimeout(() => {
      devLog('📊 EXECUTING navigation to workflow report');
      try {
        navigate('/workflow-report', { state: reportData });
        devLog('✅ Navigation successful');
      } catch (error) {
        console.error('❌ Navigation error:', error);
        alert(`Navigation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }, 100);
  };

  const handleLossRunDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      // Legacy format - just an array of documents
      setWorkflowData((prev: any) => ({ ...prev, lossRunDocuments: documents }));
    } else {
      // New enhanced format with past documents and reports
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        lossRunDocuments: documents.allDocuments, // For backward compatibility
        pastDocuments: documents.pastDocuments,
        pastReports: documents.pastReports,
        lossRunData: documents // Store the full enhanced data
      }));
    }
    // Don't auto-advance - let user click Next button when ready
  };

  const handleLossRunAnalysisComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, analysisReport }));

    // Navigate directly to full report page for loss run analysis
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: workflowType,
      documents: workflowData.lossRunDocuments || workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: workflowData.selectedCustomer ? (workflowData.selectedCustomer.lineType === 'personal' ? workflowData.selectedCustomer.name : (workflowData.selectedCustomer.company || workflowData.selectedCustomer.name)) : clientName,
      citations: analysisReport.citations || [],
      projectId: projectId
    };

    // Check if modal is closed OR this workflow was sent to background - show toast instead of auto-navigating
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      // Store report data for later viewing
      storeData(reportData);

      // Remove the local background report since it's now complete
      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      // Show toast notification with action to view report
      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Loss Run Analysis for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });

      return;
    }

    devLog('📊 Auto-navigating to loss run analysis report:', reportData);

    // Store data using the safe route data hook
    storeData(reportData);
    devLog('✅ Stored report data using safe route data hook');

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // ===== UNIVERSAL ANALYSIS HANDLERS =====

  const handleUniversalWorkflowSelect = (workflowType: string) => {
    setWorkflowData((prev: any) => ({ ...prev, selectedUniversalWorkflow: workflowType }));
    setCurrentStep(currentStep + 1);
  };

  const handleUniversalDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      // Legacy format - just an array of documents
      setWorkflowData((prev: any) => ({ ...prev, selectedDocuments: documents }));
    } else {
      // New enhanced format with past documents and reports
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        selectedDocuments: documents.allDocuments, // For backward compatibility
        documentSelectionData: documents // Store the full enhanced data
      }));
    }
    setCurrentStep(currentStep + 1);
  };

  // Handle continue in background
  const handleContinueInBackground = async () => {
    if (!currentWorkflowId) return;

    // Get workflow info - supports both universal analysis and child component workflows
    const { selectedDocuments, selectedUniversalWorkflow, currentExecutingWorkflow } = workflowData;
    const workflowTypeToUse = selectedUniversalWorkflow || currentExecutingWorkflow?.workflowType || workflowType;
    const documentsToUse = selectedDocuments || currentExecutingWorkflow?.documents || [];

    const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
    const token = localStorage.getItem('riskninja-token');

    try {
      // Mark workflow as background on the server
      // Pass customerId and workflowType so server can find the workflow even with temp IDs
      const backgroundResponse = await fetch(`${API_BASE_URL}/api/workflows/${currentWorkflowId}/background`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerId: clientId,
          workflowType: workflowTypeToUse
        })
      });

      if (backgroundResponse.ok) {
        const data = await backgroundResponse.json();
        devLog(`✅ Workflow marked as background on server:`, data);
      } else {
        console.log('Note: Could not mark workflow as background on server');
      }

      // Add to background reports context
      addBackgroundReport({
        workflowId: currentWorkflowId,
        workflowType: workflowTypeToUse,
        customerName: clientName,
        customerId: clientId,
        projectId: projectId,
        documents: documentsToUse?.map((d: any) => ({ id: d.id, name: d.name || d.fileName }))
      });

      devLog(`📤 Workflow ${currentWorkflowId} moved to background (type: ${workflowTypeToUse})`);

      // IMPORTANT: Mark that this workflow was sent to background
      // Store the workflow ID so completion handlers can check if this specific workflow was backgrounded
      backgroundedWorkflowIdRef.current = currentWorkflowId;

      // Clear the timer
      if (backgroundTimerRef.current) {
        clearTimeout(backgroundTimerRef.current);
        backgroundTimerRef.current = null;
      }

      // Reset state and close modal
      setLoading(false);
      setShowBackgroundOption(false);
      onClose();
    } catch (error) {
      console.error('Failed to move workflow to background:', error);
      setError('Failed to continue in background. Please wait for the report to complete.');
    }
  };

  const handleUniversalAnalysisComplete = async (additionalContext?: string) => {
    try {
      setLoading(true);
      setExecutionStartTime(new Date());
      setShowBackgroundOption(false);

      const { selectedDocuments, selectedUniversalWorkflow, additionalDocuments } = workflowData;

      if (!selectedDocuments || selectedDocuments.length === 0) {
        throw new Error('No documents selected');
      }

      if (!selectedUniversalWorkflow) {
        throw new Error('No workflow type selected');
      }

      // Extract document IDs
      const documentIds = selectedDocuments.map((d: any) => d.id);
      const additionalDocIds = additionalDocuments?.map((d: any) => d.id) || [];

      // Try to submit to job queue first
      // Note: jobsService reads token from localStorage directly, so we check localStorage
      const localStorageToken = localStorage.getItem('riskninja-token');
      if (localStorageToken) {
        try {
          devLog(`📤 Submitting ${selectedUniversalWorkflow} to job queue...`);

          const jobResponse = await jobsService.submitJob(localStorageToken, {
            workflowType: selectedUniversalWorkflow,
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            additionalDocuments: additionalDocIds,
            additionalContext: additionalContext,
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ Job queued successfully: ${jobResponse.jobId}`);

            // Add to background reports for tracking
            addBackgroundReport({
              workflowId: jobResponse.jobId,
              workflowType: selectedUniversalWorkflow,
              customerName: clientName,
              customerId: clientId,
              projectId: projectId,
              documents: selectedDocuments?.map((d: any) => ({ id: d.id, name: d.name || d.fileName }))
            });

            // Refresh reports to pick up the new job
            refreshReports();

            // Show success toast
            showToast({
              type: 'info',
              title: 'Workflow Queued',
              message: `${formatWorkflowTitle(selectedUniversalWorkflow)} for ${clientName} is processing. You'll be notified when it's ready.`,
              duration: 5000,
            });

            setLoading(false);
            onClose();
            return;
          }
        } catch (queueError) {
          // Job queue not available, fall back to synchronous execution
          devLog('⚠️ Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: Execute synchronously if job queue is not available
      devLog(`🔄 Executing ${selectedUniversalWorkflow} synchronously for ${selectedDocuments.length} documents`);

      // Start the 3-minute timer to show background option
      backgroundTimerRef.current = setTimeout(() => {
        devLog('⏰ 3-minute timer expired - showing background option');
        setShowBackgroundOption(true);
      }, BACKGROUND_TIMER_MS);

      // Execute the universal workflow
      const result = await workflowService.executeWorkflowAnalysis(
        selectedDocuments,
        selectedUniversalWorkflow,
        additionalContext,
        clientId,
        projectId
      );

      // Clear the timer since execution completed
      if (backgroundTimerRef.current) {
        clearTimeout(backgroundTimerRef.current);
        backgroundTimerRef.current = null;
      }

      // Store the workflow ID from the result
      if (result.workflowId) {
        setCurrentWorkflowId(result.workflowId);
      }

      setWorkflowData((prev: any) => ({ ...prev, analysisResult: result }));

      // Navigate to enhanced report page with Universal JSON renderer
      const reportData = {
        universalJsonResponse: result.results,
        workflowType: selectedUniversalWorkflow,
        documents: selectedDocuments,
        customerId: clientId,
        customerName: result.customerName || clientName,
        citations: result.metadata?.citationsGenerated || 0,
        isUniversalJson: true,
        projectId: projectId
      };

      // Check if modal is closed OR this workflow was sent to background - show toast instead of auto-navigating
      const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
      if (!isOpenRef.current || wasBackgrounded) {
        devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

        // Store report data for later viewing
        storeData(reportData);

        // Remove the local background report since it's now complete
        if (backgroundedWorkflowIdRef.current) {
          removeReport(backgroundedWorkflowIdRef.current);
          backgroundedWorkflowIdRef.current = null;
        }

        // Show toast notification with action to view report
        showToast({
          type: 'success',
          title: 'Report Ready',
          message: `${formatWorkflowTitle(selectedUniversalWorkflow)} for ${clientName} is complete.`,
          duration: 0, // Persist until dismissed
          action: {
            label: 'View Report',
            onClick: () => {
              navigate('/workflow-report', { state: reportData });
            }
          }
        });

        return;
      }

      devLog('📊 Auto-navigating to universal analysis report:', reportData);

      // Store data using the safe route data hook
      storeData(reportData);

      navigate('/workflow-report', { state: reportData });
      onClose();

    } catch (error) {
      // Clear the timer on error
      if (backgroundTimerRef.current) {
        clearTimeout(backgroundTimerRef.current);
        backgroundTimerRef.current = null;
      }
      console.error('Universal analysis failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to execute analysis');
    } finally {
      setLoading(false);
      setShowBackgroundOption(false);
    }
  };

  const handleUniversalCitationClick = (citation: any) => {
    workflowService.handleCitationClick(citation, navigate);
  };

  // ===== CERTIFICATE OF EFFORT HANDLERS =====

  const handleCertificateDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      setWorkflowData((prev: any) => ({ ...prev, selectedDocuments: documents }));
    } else {
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        selectedDocuments: documents.allDocuments,
        documentSelectionData: documents
      }));
    }
    // Don't auto-advance - let user click "Next" when ready
  };

  // ===== CLAIMS HANDLERS =====

  const handleClaimsDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      setWorkflowData((prev: any) => ({ ...prev, selectedDocuments: documents }));
    } else {
      setWorkflowData((prev: any) => ({
        ...prev,
        selectedDocuments: documents.allDocuments,
        pastReports: documents.pastReports || [],
        documentSelectionData: documents
      }));
    }
  };

  // Handler for claims context submission (stores context and allows proceeding to execute step)
  const handleClaimsContextSubmit = (context: string) => {
    setWorkflowData((prev: any) => ({ ...prev, additionalContext: context }));
  };

  // Handler for when Loss Run Analyzer completes
  const handleLossRunAnalyzerComplete = (result: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    devLog('🎯 Loss Run Analyzer complete:', result);

    const { selectedDocuments, documentSelectionData } = workflowData;

    // Extract content from result
    const content = result.content || result;
    devLog('📝 Using content from result:', content);

    // Navigate to report page with the content
    const reportData = {
      content: content,
      workflowType: 'loss_run_analyzer',
      documents: selectedDocuments,
      customerId: clientId,
      customerName: result.customerName || clientName,
      citations: result.citations || [],
      reportSaved: !!result.id,
      projectId: projectId,
      savedReportId: result.id || result.savedReportId
    };

    // Check if modal is closed OR this workflow was sent to background - show toast instead of auto-navigating
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      // Store report data for later viewing
      storeData(reportData);

      // Remove the local background report since it's now complete
      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      // Show toast notification with action to view report
      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Loss Run Analyzer for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });

      return;
    }

    devLog('📊 Auto-navigating to loss run analyzer report:', reportData);

    // Store data using the safe route data hook
    storeData(reportData);

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // ===== COVERAGE ANALYSIS HANDLERS =====

  const handleCoverageDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      setWorkflowData((prev: any) => ({ ...prev, selectedDocuments: documents }));
    } else {
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        selectedDocuments: documents.allDocuments,
        documentSelectionData: documents
      }));
    }
  };

  const handleScenarioDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      setWorkflowData((prev: any) => ({ ...prev, scenarioDocuments: documents }));
    } else {
      setWorkflowData((prev: any) => ({ 
        ...prev, 
        scenarioDocuments: documents.allDocuments,
        scenarioDocumentSelectionData: documents
      }));
    }
  };

  const handleCoverageAnalysisComplete = async (additionalContext?: string) => {
    try {
      devLog('🚀 Setting loading to true for Coverage Analysis');
      setLoading(true);
      setExecutionStartTime(new Date());
      setShowBackgroundOption(false);

      const { selectedDocuments, analysisMode, scenarioText, additionalDocuments } = workflowData;

      if (!selectedDocuments || selectedDocuments.length === 0) {
        throw new Error('No documents selected');
      }
      if (!analysisMode) {
        throw new Error('No analysis mode selected');
      }

      // Extract document IDs
      const documentIds = selectedDocuments.map((d: any) => d.id);
      const additionalDocIds = additionalDocuments?.map((d: any) => d.id) || [];

      // Try to submit to job queue first
      const localStorageToken = localStorage.getItem('riskninja-token');
      if (localStorageToken) {
        try {
          devLog(`📤 Submitting coverage_analysis (${analysisMode}) to job queue...`);

          const jobResponse = await jobsService.submitJob(localStorageToken, {
            workflowType: 'coverage_analysis',
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            additionalDocuments: additionalDocIds,
            additionalContext: additionalContext,
            analysisMode: analysisMode,
            scenarioText: scenarioText,
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ Coverage Analysis job queued successfully: ${jobResponse.jobId}`);

            // Add to background reports for tracking
            addBackgroundReport({
              workflowId: jobResponse.jobId,
              workflowType: 'coverage_analysis',
              customerName: clientName,
              customerId: clientId,
              projectId: projectId,
              documents: selectedDocuments?.map((d: any) => ({ id: d.id, name: d.name || d.fileName }))
            });

            // Refresh reports to pick up the new job
            refreshReports();

            // Show success toast
            showToast({
              type: 'info',
              title: 'Workflow Queued',
              message: `Coverage Analysis (${analysisMode}) for ${clientName} is processing. You'll be notified when it's ready.`,
              duration: 5000,
            });

            setLoading(false);
            onClose();
            return;
          }
        } catch (queueError) {
          // Job queue not available, fall back to synchronous execution
          devLog('⚠️ Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: Synchronous execution
      devLog(`🔄 Executing Coverage Analysis (${analysisMode}) for ${selectedDocuments.length} documents synchronously`);

      // Start the 3-minute timer to show background option
      backgroundTimerRef.current = setTimeout(() => {
        devLog('⏰ 3-minute timer expired - showing background option');
        setShowBackgroundOption(true);
      }, BACKGROUND_TIMER_MS);

      // Use the dedicated coverage analysis method following the loss run pattern
      const result = await workflowService.executeCoverageAnalysis({
        documents: selectedDocuments,
        analysisMode: analysisMode,
        additionalInfo: additionalContext,
        scenarioText: scenarioText, // Include scenario text from step 3
        clientId,
        projectId,
        reportTitle: reportTitle || undefined
      });

      // Clear the timer since execution completed
      if (backgroundTimerRef.current) {
        clearTimeout(backgroundTimerRef.current);
        backgroundTimerRef.current = null;
      }

      devLog('🎯 Raw coverage analysis result:', result);
      
      setWorkflowData((prev: any) => ({ ...prev, analysisResult: result }));
      
      // Backend now returns content directly (matching Loss Run pattern)
      const content = result.content || result;
      devLog('📝 Using content from result:', content);
      
      // Navigate to report page with the content
      const reportData = {
        content: content,
        workflowType: 'coverage_analysis',
        analysisMode: analysisMode,
        documents: selectedDocuments,
        customerId: clientId,
        customerName: result.customerName || clientName,
        citations: result.citations || [],
        reportSaved: !!result.id,
        projectId: projectId
      };

      // Check if modal is closed OR this workflow was sent to background - show toast instead of auto-navigating
      const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
      if (!isOpenRef.current || wasBackgrounded) {
        devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

        // Store report data for later viewing
        storeData(reportData);

        // Remove the local background report since it's now complete
        if (backgroundedWorkflowIdRef.current) {
          removeReport(backgroundedWorkflowIdRef.current);
          backgroundedWorkflowIdRef.current = null;
        }

        // Show toast notification with action to view report
        showToast({
          type: 'success',
          title: 'Report Ready',
          message: `Coverage Analysis for ${clientName} is complete.`,
          duration: 0, // Persist until dismissed
          action: {
            label: 'View Report',
            onClick: () => {
              navigate('/workflow-report', { state: reportData });
            }
          }
        });

        return;
      }

      devLog('📊 Auto-navigating to coverage analysis report:', reportData);

      // Store data using the safe route data hook
      storeData(reportData);

      navigate('/workflow-report', { state: reportData });
      onClose();

    } catch (error) {
      // Clear the timer on error
      if (backgroundTimerRef.current) {
        clearTimeout(backgroundTimerRef.current);
        backgroundTimerRef.current = null;
      }
      console.error('Coverage analysis failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to execute analysis');
    } finally {
      setLoading(false);
      setShowBackgroundOption(false);
    }
  };

  // ===== RATE ANALYSIS HANDLERS =====

  const handleRateAnalysisDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      setWorkflowData((prev: any) => ({ ...prev, selectedDocuments: documents }));
    } else {
      setWorkflowData((prev: any) => ({
        ...prev,
        selectedDocuments: documents.allDocuments,
        documentSelectionData: documents
      }));
    }
  };

  const handleRateAnalysisComplete = async (additionalContext?: string) => {
    try {
      devLog('🚀 Setting loading to true for Rate Analysis');
      setLoading(true);

      // Small delay to ensure loading state is visible
      await new Promise(resolve => setTimeout(resolve, 200));
      devLog('🎯 Loading state should now be visible');

      const { selectedDocuments, analysisTier } = workflowData;

      if (!selectedDocuments || selectedDocuments.length === 0) {
        throw new Error('No documents selected');
      }
      if (!analysisTier) {
        throw new Error('No analysis tier selected');
      }

      devLog(`🔄 Executing Rate Analysis (${analysisTier}) for ${selectedDocuments.length} documents`);

      // Use the dedicated rate analysis method
      const result = await workflowService.executeRateAnalysis({
        documents: selectedDocuments,
        analysisTier: analysisTier,
        additionalInfo: additionalContext,
        clientId,
        projectId
      });

      devLog('🎯 Raw rate analysis result:', result);

      setWorkflowData((prev: any) => ({ ...prev, analysisResult: result }));

      // Backend returns content directly
      const content = result.content || result;
      devLog('📝 Using content from result:', content);

      // Navigate to report page with the content
      const reportData = {
        content: content,
        workflowType: 'rate_analysis',
        analysisTier: analysisTier,
        documents: selectedDocuments,
        customerId: clientId,
        customerName: result.customerName || clientName,
        citations: result.citations || [],
        reportSaved: !!result.id,
        projectId: projectId
      };

      // Check if modal is closed OR this workflow was sent to background - show toast instead of auto-navigating
      const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
      if (!isOpenRef.current || wasBackgrounded) {
        devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

        // Store report data for later viewing
        storeData(reportData);

        // Remove the local background report since it's now complete
        if (backgroundedWorkflowIdRef.current) {
          removeReport(backgroundedWorkflowIdRef.current);
          backgroundedWorkflowIdRef.current = null;
        }

        // Show toast notification with action to view report
        showToast({
          type: 'success',
          title: 'Report Ready',
          message: `Rate Analysis for ${clientName} is complete.`,
          duration: 0, // Persist until dismissed
          action: {
            label: 'View Report',
            onClick: () => {
              navigate('/workflow-report', { state: reportData });
            }
          }
        });

        return;
      }

      devLog('📊 Auto-navigating to rate analysis report:', reportData);

      // Store data using the safe route data hook
      storeData(reportData);

      navigate('/workflow-report', { state: reportData });
      onClose();

    } catch (error) {
      console.error('Rate analysis failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to execute analysis');
    } finally {
      setLoading(false);
    }
  };

  // Handler for RateAnalysisExecution component completion
  const handleRateAnalysisExecutionComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, rateAnalysisResult: analysisReport }));

    // Navigate directly to full report page
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: 'rate_analysis',
      analysisTier: workflowData.analysisTier,
      documents: workflowData.selectedDocuments || [],
      customerId: clientId,
      customerName: analysisReport.customerName || clientName,
      citations: analysisReport.citations || [],
      reportSaved: !!analysisReport.id,
      projectId: projectId
    };

    // Check if modal is closed (user moved on) or workflow was explicitly backgrounded
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      // Store report data for later viewing
      storeData(reportData);

      // Remove the local background report since it's now complete
      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      // Show toast notification with action to view report
      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Rate Analysis for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });
      return;
    }

    devLog('📊 Auto-navigating to rate analysis report:', reportData);

    // Store data using the safe route data hook
    storeData(reportData);

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // Handler for Portfolio Analyzer document selection
  const handlePortfolioAnalyzerDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    // Handle both old array format and new enhanced format
    if (Array.isArray(documents)) {
      setWorkflowData((prev: any) => ({ ...prev, selectedDocuments: documents }));
    } else {
      setWorkflowData((prev: any) => ({
        ...prev,
        selectedDocuments: documents.allDocuments,
        documentSelectionData: documents
      }));
    }
  };

  // Handler for PortfolioAnalyzerExecution component completion
  const handlePortfolioAnalyzerExecutionComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, portfolioAnalyzerResult: analysisReport }));

    // Navigate directly to full report page
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: 'portfolio_analyzer',
      scenario: workflowData.scenario,
      documents: analysisReport.documents || workflowData.selectedDocuments || [],
      customerId: analysisReport.customerId || clientId,
      customerName: analysisReport.customerName || clientName,
      citations: analysisReport.citations || [],
      reportSaved: !!analysisReport.id || !!analysisReport.savedReportId,
      reportId: analysisReport.savedReportId || analysisReport.id,
      projectId: analysisReport.projectId || projectId,
      isUniversalJson: analysisReport.isUniversalJson ?? true,
      analysisType: analysisReport.analysisType || 'portfolio_analyzer',
      generatedAt: analysisReport.generatedAt || new Date().toISOString()
    };

    // Check if modal is closed (user moved on) or workflow was explicitly backgrounded
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      // Store report data for later viewing
      storeData(reportData);

      // Remove the local background report since it's now complete
      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      // Show toast notification with action to view report
      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Portfolio Analysis for ${clientName} is complete.`,
        duration: 0, // Persist until dismissed
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });
      return;
    }

    devLog('📊 Auto-navigating to portfolio analyzer report:', reportData);

    // Store data using the safe route data hook
    storeData(reportData);

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // ===== HOMEOWNERS COMPARISON HANDLERS =====

  // Handler for Homeowners Comparison document selection
  const handleHomeownersComparisonDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    if (Array.isArray(documents)) {
      setWorkflowData((prev: any) => ({ ...prev, selectedDocuments: documents }));
    } else {
      setWorkflowData((prev: any) => ({
        ...prev,
        selectedDocuments: documents.allDocuments,
        documentSelectionData: documents
      }));
    }
  };

  // Handler for HomeownersComparisonExecution component completion
  const handleHomeownersComparisonExecutionComplete = (analysisReport: any) => {
    // Clear the background timer since execution completed
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, homeownersComparisonResult: analysisReport }));

    // Navigate directly to full report page
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: 'homeowners_comparison',
      documents: analysisReport.documents || workflowData.selectedDocuments || [],
      customerId: analysisReport.customerId || clientId,
      customerName: analysisReport.customerName || clientName,
      citations: analysisReport.citations || [],
      reportSaved: !!analysisReport.id || !!analysisReport.savedReportId,
      reportId: analysisReport.savedReportId || analysisReport.id,
      projectId: analysisReport.projectId || projectId,
      isUniversalJson: analysisReport.isUniversalJson ?? true,
      analysisType: analysisReport.analysisType || 'homeowners_comparison',
      generatedAt: analysisReport.generatedAt || new Date().toISOString()
    };

    // Check if modal is closed (user moved on) or workflow was explicitly backgrounded
    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      storeData(reportData);

      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Homeowners Insurance Comparison for ${clientName} is complete.`,
        duration: 0,
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });
      return;
    }

    devLog('📊 Auto-navigating to homeowners comparison report:', reportData);

    storeData(reportData);

    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // ===== PERSONAL UMBRELLA HANDLERS =====
  const handlePersonalUmbrellaDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    if (Array.isArray(documents)) {
      setWorkflowData((prev: any) => ({ ...prev, selectedDocuments: documents }));
    } else {
      setWorkflowData((prev: any) => ({
        ...prev,
        selectedDocuments: documents.allDocuments,
        documentSelectionData: documents
      }));
    }
  };

  const handlePersonalUmbrellaQuestionnaireSubmit = useCallback((questionnaireData: Record<string, any>) => {
    setWorkflowData((prev: any) => ({ ...prev, questionnaireData }));
  }, []);

  const handlePersonalUmbrellaExecutionComplete = (analysisReport: any) => {
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, personalUmbrellaResult: analysisReport }));

    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: 'personal_umbrella',
      documents: analysisReport.documents || workflowData.selectedDocuments || [],
      customerId: analysisReport.customerId || clientId,
      customerName: analysisReport.customerName || clientName,
      citations: analysisReport.citations || [],
      reportSaved: !!analysisReport.id || !!analysisReport.savedReportId,
      reportId: analysisReport.savedReportId || analysisReport.id,
      projectId: analysisReport.projectId || projectId,
      isUniversalJson: analysisReport.isUniversalJson ?? true,
      analysisType: analysisReport.analysisType || 'personal_umbrella',
      generatedAt: analysisReport.generatedAt || new Date().toISOString()
    };

    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      storeData(reportData);

      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `Personal Umbrella Analysis for ${clientName} is complete.`,
        duration: 0,
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });
      return;
    }

    storeData(reportData);
    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // ===== CYBER BETA SUITE HANDLERS =====
  // Shared by all 5 cyber_* workflow types — generic doc-select + execution
  // handlers, parameterized by the outer `workflowType` prop instead of a
  // hardcoded literal per tool. See CYBER_BETA_TOOLS_PLAN.md Phase 2.
  const handleCyberSuiteDocumentSelect = (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => {
    if (Array.isArray(documents)) {
      setWorkflowData((prev: any) => ({ ...prev, selectedDocuments: documents }));
    } else {
      setWorkflowData((prev: any) => ({
        ...prev,
        selectedDocuments: documents.allDocuments,
        documentSelectionData: documents
      }));
    }
  };

  const handleCyberSuiteExecutionComplete = (analysisReport: any) => {
    if (backgroundTimerRef.current) {
      clearTimeout(backgroundTimerRef.current);
      backgroundTimerRef.current = null;
    }
    setShowBackgroundOption(false);

    setWorkflowData((prev: any) => ({ ...prev, cyberSuiteResult: analysisReport }));

    const resolvedWorkflowType = analysisReport.analysisType || workflowType;
    const reportData = {
      content: analysisReport.content || analysisReport,
      workflowType: resolvedWorkflowType,
      documents: analysisReport.documents || workflowData.selectedDocuments || [],
      customerId: analysisReport.customerId || clientId,
      customerName: analysisReport.customerName || clientName,
      citations: analysisReport.citations || [],
      reportSaved: !!analysisReport.id || !!analysisReport.savedReportId,
      reportId: analysisReport.savedReportId || analysisReport.id,
      projectId: analysisReport.projectId || projectId,
      isUniversalJson: analysisReport.isUniversalJson ?? true,
      analysisType: resolvedWorkflowType,
      generatedAt: analysisReport.generatedAt || new Date().toISOString()
    };

    const wasBackgrounded = backgroundedWorkflowIdRef.current !== null;
    if (!isOpenRef.current || wasBackgrounded) {
      devLog('📬 Background cyber workflow completed - showing toast notification', { isOpenRef: isOpenRef.current, wasBackgrounded });

      storeData(reportData);

      if (backgroundedWorkflowIdRef.current) {
        removeReport(backgroundedWorkflowIdRef.current);
        backgroundedWorkflowIdRef.current = null;
      }

      showToast({
        type: 'success',
        title: 'Report Ready',
        message: `${formatWorkflowTitle(resolvedWorkflowType)} for ${clientName} is complete.`,
        duration: 0,
        action: {
          label: 'View Report',
          onClick: () => {
            navigate('/workflow-report', { state: reportData });
          }
        }
      });
      return;
    }

    storeData(reportData);
    navigate('/workflow-report', { state: reportData });
    onClose();
  };

  // ===== CYBER BETA SUITE STEPS =====
  // Generic doc-selection -> optional-context -> run flow, shared by all 5
  // types. cyber_auto gets a distinct context-box label since that text
  // feeds the router.
  const getCyberSuiteSteps = (): WorkflowStep[] => {
    const isAutoRouter = workflowType.endsWith('_auto');
    return [
    {
      id: 'document_upload',
      title: 'Upload Documents',
      component: (
        <DocumentSourceSelection
          clientId={clientId}
          projectId={projectId}
          onSelect={handleCyberSuiteDocumentSelect}
          workflowType={workflowType}
          initialPastDocumentIds={workflowData.initialSelectedDocumentIds}
          suggestionsLoading={catalogSuggestionsLoading}
        />
      ),
      // Tools with consumesReportOf (e.g. lossrun_mitigation_wc) can proceed
      // on a selected past report alone, with zero uploaded documents.
      canProceed:
        (!!workflowData.selectedDocuments && workflowData.selectedDocuments.length > 0) ||
        (!!workflowData.documentSelectionData?.pastReports && workflowData.documentSelectionData.pastReports.length > 0)
    },
    {
      id: 'additional_context',
      // Auto-routers lead with the goal question: the typed text outweighs the
      // document signature in the router's classification call, so it deserves
      // a dedicated step rather than a buried optional textarea.
      title: isAutoRouter ? 'What are you looking to do?' : 'Add Additional Context',
      component: isAutoRouter ? (
        <RouterGoalStep
          workflowType={workflowType}
          onContextSubmit={handleContextSubmit}
          onSkip={() => setCurrentStep(step => step + 1)}
          initialValue={workflowData.initialContext}
        />
      ) : (
        <AdditionalContextStep
          onContextSubmit={handleContextSubmit}
          placeholder={'Add any specific instructions or context for this analysis (e.g., focus areas, known concerns)...'}
          customerId={clientId}
          projectId={projectId}
          initialValue={workflowData.initialContext}
        />
      ),
      canProceed: true,
      nextLabel: isAutoRouter && (workflowData.additionalContext || '').trim()
        ? 'Continue with this goal'
        : undefined
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <CyberSuiteExecution
        workflowType={workflowType}
        documents={workflowData.selectedDocuments || []}
        additionalContext={workflowData.additionalContext || ''}
        pastReports={workflowData.documentSelectionData?.pastReports}
        additionalInfo={workflowData.additionalContext || undefined}
        clientId={clientId}
        clientName={clientName}
        projectId={projectId}
        onComplete={handleCyberSuiteExecutionComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, workflowType, workflowData.selectedDocuments || [])}
        token={token}
        loading={loading}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.cyberSuiteResult
    }
  ];
  };

  const handleCertificateComplete = async (result: any) => {
    setWorkflowData((prev: any) => ({ ...prev, certificateResult: result }));
    
    // Save COE report to database using the actual comparison reports API
    try {
      const documents = workflowData.selectedDocuments || [];
      const reportTitle = `Certificate of Effort - ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
      
      // Structure the content for proper PDF viewing in reports tab
      const structuredContent = {
        message: result.message || 'Certificate of Effort generated successfully',
        certificateData: result.certificateData,
        downloadUrl: result.originalPdfData || result.downloadUrl, // Store original base64 data
        workflowType: 'certificate_of_effort',
        generatedAt: new Date().toISOString(),
        // Store the PDF URL for direct access
        pdfUrl: result.originalPdfData || result.downloadUrl
      };

      const reportData = {
        title: reportTitle,
        content: JSON.stringify(structuredContent), // Properly structured content for database
        documentNames: documents.map((doc: any) => doc.name || 'Unknown Document'),
        documentIds: documents.map((doc: any) => doc.id),
        primaryPolicyType: 'certificate_of_effort', // Use COE as policy type
        additionalFacts: JSON.stringify({
          pdfUrl: result.originalPdfData || result.downloadUrl, // Use original base64 data for permanent storage
          certificateType: 'certificate_of_effort',
          openAs: 'pdf_viewer', // Explicit instruction to open in PDF viewer
          isPdfReport: true // Additional flag for clear identification
        }),
        customerId: clientId,
        projectId: projectId
      };

      devLog('📄 Saving Certificate of Effort to database:', reportTitle);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/comparison-reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify(reportData)
      });

      if (response.ok) {
        const savedReport = await response.json();
        devLog('✅ Certificate of Effort saved to Reports tab successfully:', savedReport.title);
      } else {
        console.error('❌ Failed to save COE report:', await response.text());
      }
    } catch (error) {
      console.error('❌ Error saving COE report:', error);
    }

    devLog('📄 Certificate generation complete - staying in modal for preview/download');
  };


  const getCertificateOfEffortSteps = (): WorkflowStep[] => [
    {
      id: 'document_upload',
      title: 'Upload Documents',
      component: (
        <DocumentSourceSelection
          clientId={clientId}
          projectId={projectId}
          onSelect={handleCertificateDocumentSelect}
          workflowType="certificate_of_effort"
        />
      ),
      canProceed: !!workflowData.selectedDocuments && workflowData.selectedDocuments.length > 0
    },
    {
      id: 'certificate_generation',
      title: 'Generate Certificate of Effort',
      component: (
        <CertificateOfEffortGeneration 
          documents={workflowData.selectedDocuments || []}
          clientId={clientId}
          clientName={clientName}
          projectId={projectId}
          onComplete={handleCertificateComplete}
          loading={loading}
        />
      ),
      canProceed: !!workflowData.certificateResult
    }
  ];

  // Standalone Loss Run Analyzer workflow (separate from claims tool)
  const getLossRunAnalyzerSteps = (): WorkflowStep[] => [
    {
      id: 'document_selection',
      title: 'Select Documents',
      component: (
        <DocumentSourceSelection
          clientId={clientId}
          projectId={projectId}
          onSelect={handleClaimsDocumentSelect}
          workflowType="loss_run_analyzer"
        />
      ),
      canProceed: !!workflowData.selectedDocuments && workflowData.selectedDocuments.length > 0
    },
    {
      id: 'additional_context',
      title: 'Additional Context',
      component: (
        <AdditionalContextStep
          onContextSubmit={handleClaimsContextSubmit}
          placeholder="Add any specific instructions or focus areas for the loss run analysis (e.g., 'Focus on workers compensation claims' or 'Analyze claims over $50,000')..."
          customerId={clientId}
          projectId={projectId}
        />
      ),
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: (
        <LossRunAnalyzer
          documents={workflowData.selectedDocuments || []}
          additionalContext={workflowData.additionalContext || ''}
          clientId={clientId}
          projectId={projectId}
          pastReports={workflowData.documentSelectionData?.pastReports || []}
          onComplete={handleLossRunAnalyzerComplete}
          onError={handleWorkflowError}
          onExecutionStart={handleChildExecutionStart}
          onJobQueued={(jobId) => handleJobQueued(jobId, 'loss_run_analyzer', workflowData.selectedDocuments || [])}
          token={token}
          loading={loading}
          reportTitle={reportTitle}
          onReportTitleChange={setReportTitle}
        />
      ),
      canProceed: !!workflowData.analysisResult
    }
  ];

  const getLossRunAnalyzerV4Steps = (): WorkflowStep[] => [
    {
      id: 'document_selection',
      title: 'Select Documents',
      component: (
        <DocumentSourceSelection
          clientId={clientId}
          projectId={projectId}
          onSelect={handleClaimsDocumentSelect}
          workflowType="loss_run_analyzer_v4"
          initialPastDocumentIds={workflowData.initialSelectedDocumentIds}
        suggestionsLoading={catalogSuggestionsLoading}
        />
      ),
      canProceed: !!workflowData.selectedDocuments && workflowData.selectedDocuments.length > 0
    },
    {
      id: 'additional_context',
      title: 'Additional Context',
      component: (
        <AdditionalContextStep
          onContextSubmit={handleClaimsContextSubmit}
          placeholder="Add any specific instructions or focus areas for the loss run analysis (e.g., 'Focus on workers compensation claims' or 'Analyze claims over $50,000')..."
          customerId={clientId}
          projectId={projectId}
        />
      ),
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: (
        <LossRunAnalyzer
          documents={workflowData.selectedDocuments || []}
          additionalContext={workflowData.additionalContext || ''}
          clientId={clientId}
          projectId={projectId}
          pastReports={workflowData.documentSelectionData?.pastReports || []}
          onComplete={handleLossRunAnalyzerComplete}
          onError={handleWorkflowError}
          onExecutionStart={handleChildExecutionStart}
          onJobQueued={(jobId) => handleJobQueued(jobId, 'loss_run_analyzer_v4', workflowData.selectedDocuments || [])}
          token={token}
          loading={loading}
          reportTitle={reportTitle}
          onReportTitleChange={setReportTitle}
          workflowType="loss_run_analyzer_v4"
        />
      ),
      canProceed: !!workflowData.analysisResult
    }
  ];

  const getLossRunAnalyzerV4_3Steps = (): WorkflowStep[] => [
    {
      id: 'document_selection',
      title: 'Select Documents',
      component: (
        <DocumentSourceSelection
          clientId={clientId}
          projectId={projectId}
          onSelect={handleClaimsDocumentSelect}
          workflowType="loss_run_analyzer_v4_3"
        />
      ),
      canProceed: !!workflowData.selectedDocuments && workflowData.selectedDocuments.length > 0
    },
    {
      id: 'additional_context',
      title: 'Additional Context',
      component: (
        <AdditionalContextStep
          onContextSubmit={handleClaimsContextSubmit}
          placeholder="Add any specific instructions or focus areas for the loss run analysis (e.g., 'Focus on workers compensation claims' or 'Analyze claims over $50,000')..."
          customerId={clientId}
          projectId={projectId}
        />
      ),
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: (
        <LossRunAnalyzer
          documents={workflowData.selectedDocuments || []}
          additionalContext={workflowData.additionalContext || ''}
          clientId={clientId}
          projectId={projectId}
          pastReports={workflowData.documentSelectionData?.pastReports || []}
          onComplete={handleLossRunAnalyzerComplete}
          onError={handleWorkflowError}
          onExecutionStart={handleChildExecutionStart}
          onJobQueued={(jobId) => handleJobQueued(jobId, 'loss_run_analyzer_v4_3', workflowData.selectedDocuments || [])}
          token={token}
          loading={loading}
          reportTitle={reportTitle}
          onReportTitleChange={setReportTitle}
          workflowType="loss_run_analyzer_v4_3"
        />
      ),
      canProceed: !!workflowData.analysisResult
    }
  ];

  const getCoverageAnalysisSteps = (): WorkflowStep[] => [
    {
      id: 'document_upload',
      title: 'Upload Policy Documents',
      component: (
        <DocumentSourceSelection
          clientId={clientId}
          projectId={projectId}
          onSelect={handleCoverageDocumentSelect}
          workflowType="coverage_analysis"
          initialPastDocumentIds={workflowData.initialSelectedDocumentIds}
        suggestionsLoading={catalogSuggestionsLoading}
        />
      ),
      canProceed: !!workflowData.selectedDocuments && workflowData.selectedDocuments.length > 0
    },
    {
      id: 'analysis_mode_selection',
      title: 'Select Analysis Mode',
      component: (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Choose Coverage Analysis Mode
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Select the type of coverage analysis you want to perform
            </p>
          </div>
          
          <div className="grid gap-4">
            {[
              {
                id: 'Policy Clause Analysis',
                title: 'Policy Clause Analysis',
                description: 'Analyze specific policy clauses, terms, conditions, and coverage provisions to identify coverage gaps, ambiguities, and potential issues.',
                icon: '📋'
              },
              {
                id: 'Policy Scenario Analysis',
                title: 'Policy Scenario Analysis',
                description: 'Evaluate how the policy would respond to specific loss scenarios and real-world situations.',
                icon: '🎯'
              },
              {
                id: 'General Review',
                title: 'General Review',
                description: 'Comprehensive policy review relative to your client\'s risk profile. REQUIRES: Policy document + Client Risk Assessment document.',
                icon: '🔍'
              }
            ].map((mode) => (
              <button
                key={mode.id}
                onClick={() => setWorkflowData({ ...workflowData, analysisMode: mode.id })}
                className={`p-4 border rounded-lg text-left transition-colors ${
                  workflowData.analysisMode === mode.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">{mode.icon}</span>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">{mode.title}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{mode.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ),
      canProceed: !!workflowData.analysisMode
    },
    {
      id: 'scenario_input',
      title: 'Scenario Information (Optional)',
      component: (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Scenario Information
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Optionally describe specific scenarios or situations for more targeted analysis
            </p>
          </div>

          {/* Scenario Text Input */}
          <div className="border rounded-lg p-4 dark:border-gray-600">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Scenario Description</h4>
            <textarea
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe any specific scenarios, loss events, or situations you want to analyze against the policy coverage..."
              value={workflowData.scenarioText || ''}
              onChange={(e) => setWorkflowData({ ...workflowData, scenarioText: e.target.value })}
            />
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              💡 <strong>Tip:</strong> This step is optional. You can proceed without additional information,
              or provide scenario details to get more targeted coverage analysis. You can also upload additional documents in the next step.
            </p>
          </div>
        </div>
      ),
      canProceed: true // This step is always optional
    },
    {
      id: 'additional_context',
      title: 'Additional Context (Optional)',
      component: (
        <UniversalContextInput
          onSubmit={handleCoverageAnalysisComplete}
          loading={loading}
          workflowType="coverage_analysis"
          documents={workflowData.selectedDocuments || []}
          customerId={clientId}
          projectId={projectId}
          reportTitle={reportTitle}
          onReportTitleChange={setReportTitle}
        />
      ),
      canProceed: true
    }
  ];

  // ===== RATE ANALYSIS STEPS =====
  const getRateAnalysisSteps = (): WorkflowStep[] => [
    {
      id: 'document_upload',
      title: 'Upload Rating Documents',
      component: (
        <DocumentSourceSelection
          clientId={clientId}
          projectId={projectId}
          onSelect={handleRateAnalysisDocumentSelect}
          workflowType="rate_analysis"
        />
      ),
      canProceed: !!workflowData.selectedDocuments && workflowData.selectedDocuments.length > 0
    },
    {
      id: 'tier_selection',
      title: 'Select Analysis Tier',
      component: (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Choose Rate Analysis Tier
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Select the depth of premium development analysis you want to perform
            </p>
          </div>

          <div className="grid gap-4">
            {[
              {
                id: 'tier1',
                title: 'Tier 1: Rate & Classification Analysis',
                description: 'Analyzes rate changes, classification changes, and their relative contribution to premium changes.',
                icon: '📊'
              },
              {
                id: 'tier2',
                title: 'Tier 2: Rating Basis Analysis',
                description: 'Analyzes changes in exposure bases (payroll, sales, units, area) and their contribution to premium changes.',
                icon: '📐'
              },
              {
                id: 'tier3',
                title: 'Tier 3: Integrated Analysis',
                description: 'Comprehensive analysis combining Rate, Classification, and Basis analysis with driver hierarchy and interaction effects.',
                icon: '🔍'
              },
              {
                id: 'tier4',
                title: 'Tier 4: Temporal Pattern Recognition',
                description: 'Advanced multi-year analysis detecting weak signals and market cycle patterns. Requires 3+ years of data.',
                icon: '📈',
                warning: 'Requires 3+ years of policy data'
              }
            ].map((tier) => (
              <button
                key={tier.id}
                onClick={() => setWorkflowData({ ...workflowData, analysisTier: tier.id })}
                className={`p-4 border rounded-lg text-left transition-colors ${
                  workflowData.analysisTier === tier.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">{tier.icon}</span>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">{tier.title}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{tier.description}</p>
                    {tier.warning && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {tier.warning}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ),
      canProceed: !!workflowData.analysisTier
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific instructions or context for the rate analysis (e.g., focus areas, known changes, specific coverage lines)..."
        customerId={clientId}
        projectId={projectId}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <RateAnalysisExecution
        documents={workflowData.selectedDocuments || []}
        analysisTier={workflowData.analysisTier || 'tier1'}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        clientName={clientName}
        projectId={projectId}
        onComplete={handleRateAnalysisExecutionComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'rate_analysis', workflowData.selectedDocuments || [])}
        token={token}
        loading={loading}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.rateAnalysisResult
    }
  ];

  // ===== PORTFOLIO ANALYZER STEPS =====
  const getPortfolioAnalyzerSteps = (): WorkflowStep[] => [
    {
      id: 'document_upload',
      title: 'Upload Policy Documents',
      component: (
        <DocumentSourceSelection
          clientId={clientId}
          projectId={projectId}
          onSelect={handlePortfolioAnalyzerDocumentSelect}
          workflowType="portfolio_analyzer"
        />
      ),
      canProceed: !!workflowData.selectedDocuments && workflowData.selectedDocuments.length > 0
    },
    {
      id: 'scenario_input',
      title: 'Describe Scenario',
      component: (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Describe the Scenario to Analyze
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Provide a description of the loss scenario you want to trace through the portfolio
            </p>
          </div>

          <div className="space-y-4">
            <textarea
              value={workflowData.scenario || ''}
              onChange={(e) => setWorkflowData({ ...workflowData, scenario: e.target.value })}
              placeholder="Describe the scenario to analyze (e.g., 'A data breach occurs resulting in the loss of 500,000 customer records, subsequent regulatory investigation, and class action lawsuit...')"
              className="w-full h-40 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              The more detail you provide, the more accurate the coverage tracing will be. Include information about the type of loss, damages, third-party involvement, and any other relevant factors.
            </p>
          </div>
        </div>
      ),
      canProceed: true  // Scenario is optional - portfolio structure analysis can proceed without it
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any additional context about the portfolio or scenario (e.g., specific coverage concerns, known limitations, recent policy changes)..."
        customerId={clientId}
        projectId={projectId}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <PortfolioAnalyzerExecution
        documents={workflowData.selectedDocuments || []}
        scenario={workflowData.scenario || ''}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        clientName={clientName}
        projectId={projectId}
        onComplete={handlePortfolioAnalyzerExecutionComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'portfolio_analyzer', workflowData.selectedDocuments || [])}
        token={token}
        loading={loading}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.portfolioAnalyzerResult
    }
  ];

  // ===== HOMEOWNERS COMPARISON STEPS =====
  const getHomeownersComparisonSteps = (): WorkflowStep[] => [
    {
      id: 'document_upload',
      title: 'Upload Insurance Documents',
      component: (
        <DocumentSourceSelection
          clientId={clientId}
          projectId={projectId}
          onSelect={handleHomeownersComparisonDocumentSelect}
          workflowType="homeowners_comparison"
        />
      ),
      canProceed: !!workflowData.selectedDocuments && workflowData.selectedDocuments.length > 0
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific context about the property, coverage concerns, or comparison focus areas (e.g., 'Focus on flood and wind coverage adequacy', 'Property has a detached guest house used as Airbnb')..."
        customerId={clientId}
        projectId={projectId}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Comparison',
      component: <HomeownersComparisonExecution
        documents={workflowData.selectedDocuments || []}
        additionalContext={workflowData.additionalContext || ''}
        clientId={clientId}
        clientName={clientName}
        projectId={projectId}
        onComplete={handleHomeownersComparisonExecutionComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'homeowners_comparison', workflowData.selectedDocuments || [])}
        token={token}
        loading={loading}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.homeownersComparisonResult
    }
  ];

  // ===== PERSONAL UMBRELLA STEPS =====
  const getPersonalUmbrellaSteps = (): WorkflowStep[] => [
    {
      id: 'document_upload',
      title: 'Upload Policy Documents',
      component: (
        <DocumentSourceSelection
          clientId={clientId}
          projectId={projectId}
          onSelect={handlePersonalUmbrellaDocumentSelect}
          workflowType="personal_umbrella"
        />
      ),
      canProceed: !!workflowData.selectedDocuments && workflowData.selectedDocuments.length > 0
    },
    {
      id: 'questionnaire',
      title: 'Liability Exposure Questionnaire',
      component: <PersonalUmbrellaQuestionnaire
        onSubmit={handlePersonalUmbrellaQuestionnaireSubmit}
      />,
      canProceed: true
    },
    {
      id: 'additional_context',
      title: 'Add Additional Context',
      component: <AdditionalContextStep
        onContextSubmit={handleContextSubmit}
        placeholder="Add any specific context about the client's umbrella coverage needs, underlying policies, or analysis focus areas (e.g., 'Client has rental properties', 'Focus on drop-down coverage gaps')..."
        customerId={clientId}
        projectId={projectId}
      />,
      canProceed: true
    },
    {
      id: 'execute_analysis',
      title: 'Execute Analysis',
      component: <PersonalUmbrellaExecution
        documents={workflowData.selectedDocuments || []}
        additionalContext={workflowData.additionalContext || ''}
        questionnaireData={workflowData.questionnaireData}
        clientId={clientId}
        clientName={clientName}
        projectId={projectId}
        onComplete={handlePersonalUmbrellaExecutionComplete}
        onError={handleWorkflowError}
        onExecutionStart={handleChildExecutionStart}
        onJobQueued={(jobId) => handleJobQueued(jobId, 'personal_umbrella', workflowData.selectedDocuments || [])}
        token={token}
        loading={loading}
        reportTitle={reportTitle}
        onReportTitleChange={setReportTitle}
      />,
      canProceed: !!workflowData.personalUmbrellaResult
    }
  ];

  // Continue to end of getCoverageAnalysisSteps with proper closing
  const getUniversalAnalysisSteps = (): WorkflowStep[] => [
    {
      id: 'workflow_selection',
      title: 'Select Analysis Type',
      component: (
        <WorkflowSelector 
          documents={workflowData.selectedDocuments || []}
          onExecute={handleUniversalWorkflowSelect}
          loading={loading}
        />
      ),
      canProceed: !!workflowData.selectedUniversalWorkflow
    },
    {
      id: 'document_selection',
      title: 'Select Documents',
      component: (
        <DocumentSourceSelection
          clientId={clientId}
          projectId={projectId}
          onSelect={handleUniversalDocumentSelect}
          workflowType={workflowData.selectedUniversalWorkflow || 'universal_analysis'}
        />
      ),
      canProceed: !!workflowData.selectedDocuments && workflowData.selectedDocuments.length > 0
    },
    {
      id: 'additional_context',
      title: 'Additional Context (Optional)',
      component: (
        <UniversalContextInput
          onSubmit={handleUniversalAnalysisComplete}
          loading={loading}
          workflowType={workflowData.selectedUniversalWorkflow}
          documents={workflowData.selectedDocuments || []}
          customerId={clientId}
          projectId={projectId}
          reportTitle={reportTitle}
          onReportTitleChange={setReportTitle}
        />
      ),
      canProceed: true
    }
  ];

  const steps = useMemo(() => {
    console.log('Regenerating steps with comparison type:', workflowData.comparisonType);
    console.log('Regenerating steps with workflow data:', {
      quoteBinder: !!workflowData.quoteBinder,
      policy: !!workflowData.policy,
      selectedDocuments: !!workflowData.selectedDocuments
    });
    return getSteps();
  }, [workflowType, workflowData, forceUpdate, reportTitle]);
  const currentStepData = steps[currentStep];

  // Express mode: auto-advance past a step the instant its data requirement
  // is satisfied (mirrors clicking "Next" the moment it becomes enabled).
  // Stops on its own once the execute step is reached — that step still
  // requires its own explicit Run click, same as every other launch path.
  useEffect(() => {
    if (!expressMode) return;
    if (currentStep >= steps.length - 1) return;
    if (!currentStepData?.canProceed) return;
    setCurrentStep((s) => Math.min(steps.length - 1, s + 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expressMode, currentStep, currentStepData?.canProceed, steps.length]);

  if (!isOpen || !currentStepData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
        {/* Background Option Overlay - shows for both parent-managed and child-managed loading */}
        {showBackgroundOption && (
          <div className="absolute inset-0 bg-white/95 dark:bg-gray-800/95 z-10 flex items-center justify-center">
            <div className="text-center p-8 max-w-md">
              <div className="mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Report Taking Longer Than Expected
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  This analysis is taking a bit longer to generate. You can continue using the platform and we'll notify you when it's ready.
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleContinueInBackground}
                  className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                >
                  Continue in Background
                </button>
                <button
                  onClick={() => setShowBackgroundOption(false)}
                  className="w-full px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Wait for Report
                </button>
              </div>

              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                You'll receive a notification when your report is ready.
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {formatWorkflowTitle(workflowType)} Workflow
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                <span className="capitalize">{clientType}:</span> {clientName}
                {contactName && contactName !== clientName && (
                  <> · Contact: {contactName}</>
                )}
                {projectName && (
                  <> · Project: {projectName}</>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Express launch indicator — recommended-card launches skip straight
              to the execute step; this is the one place that's visible. */}
          {expressMode && currentStep === steps.length - 1 && (
            <div className="mt-3 flex items-center justify-between gap-3 px-3 py-2 bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 rounded-lg text-sm">
              <span className="text-secondary dark:text-dark-text">
                Launched with preselected documents{initialSelectedDocumentIds ? ` (${initialSelectedDocumentIds.length})` : ''}.
              </span>
              <button
                onClick={() => {
                  setExpressMode(false);
                  setCurrentStep(0);
                }}
                className="flex-shrink-0 text-primary hover:underline font-medium"
              >
                Adjust selection
              </button>
            </div>
          )}

          {/* Progress indicator */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>Step {currentStep + 1} of {steps.length}</span>
              <span>{currentStepData.title}</span>
            </div>
            <div className="mt-2 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-primary rounded-full h-2 transition-all"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
              {error}
            </div>
          )}
          
          {currentStepData.component}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
          {currentStep > 0 ? (
            <button
              onClick={() => {
                setExpressMode(false);
                setCurrentStep(Math.max(0, currentStep - 1));
              }}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Previous
            </button>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-3">
            {savedWorkflowId && (
              <span className="text-sm text-green-600 dark:text-green-400 flex items-center">
                ✓ Saved
              </span>
            )}

            {currentStep < steps.length - 1 && (
              <>
                {!currentStepData.canProceed && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Complete this step to continue
                  </span>
                )}
                <button
                  onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
                  disabled={!currentStepData.canProceed}
                  title={!currentStepData.canProceed ? 'Complete this step to continue' : undefined}
                  className={`px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
                    currentStepData.canProceed ? 'animate-pulse-prominent' : ''
                  }`}
                >
                  {currentStepData.nextLabel || 'Next'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Components
const BusinessTypeSelection: React.FC<{ onSelect: (type: 'existing_business' | 'new_business') => void }> = ({ onSelect }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Select Business Type</h3>
    <div className="grid grid-cols-2 gap-4">
      <button
        onClick={() => onSelect('existing_business')}
        className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
      >
        <h4 className="font-medium text-gray-900 dark:text-white">Existing Business</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Research existing companies and analyze current policies
        </p>
      </button>
      <button
        onClick={() => onSelect('new_business')}
        className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
      >
        <h4 className="font-medium text-gray-900 dark:text-white">New Business</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Process completely new business applications
        </p>
      </button>
    </div>
  </div>
);

const ToolSelection: React.FC<{ 
  businessType: string;
  onSelect: (tool: 'research_tool' | 'loss_run_tool') => void;
}> = ({ businessType, onSelect }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Select Tool</h3>
    <div className="grid grid-cols-2 gap-4">
      <button
        onClick={() => onSelect('research_tool')}
        className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
      >
        <h4 className="font-medium text-gray-900 dark:text-white">Research Tool</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Research company background and risk factors
        </p>
      </button>
      <button
        onClick={() => onSelect('loss_run_tool')}
        className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
      >
        <h4 className="font-medium text-gray-900 dark:text-white">Loss Run Tool</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Analyze loss history and claims data
        </p>
      </button>
    </div>
  </div>
);

// ===== UNIVERSAL CONTEXT INPUT COMPONENT =====
// Updated to match AdditionalContextStep styling with PastDocumentSelector

interface UniversalContextInputProps {
  onSubmit: (additionalContext?: string, pastDocuments?: any[], pastReports?: any[], additionalFiles?: any[]) => void;
  loading: boolean;
  workflowType?: string;
  documents: any[];
  customerId?: string;
  projectId?: string;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}

const UniversalContextInput: React.FC<UniversalContextInputProps> = ({
  onSubmit,
  loading,
  workflowType,
  documents,
  customerId,
  projectId,
  reportTitle,
  onReportTitleChange
}) => {
  const [additionalContext, setAdditionalContext] = useState('');
  const [isInternalLoading, setIsInternalLoading] = useState(false);
  const [selectedPastDocuments, setSelectedPastDocuments] = useState<any[]>([]);
  const [selectedPastReports, setSelectedPastReports] = useState<any[]>([]);
  const [additionalFiles, setAdditionalFiles] = useState<any[]>([]);

  // Use internal loading state OR prop loading state
  const effectiveLoading = isInternalLoading || loading;

  // Manual submission when user clicks button
  const handleSubmit = () => {
    devLog('🚀 UniversalContextInput: User clicked Generate Report button');
    setIsInternalLoading(true);
    onSubmit(
      additionalContext.trim() || undefined,
      selectedPastDocuments,
      selectedPastReports,
      additionalFiles
    );
  };

  const handlePastDocumentsSelected = (documents: any[]) => {
    setSelectedPastDocuments(documents);
  };

  const handlePastReportsSelected = (reports: any[]) => {
    setSelectedPastReports(reports);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
        Add Additional Context
      </h3>

      {/* Past Documents and Reports Selection - matching AdditionalContextStep */}
      {customerId && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 dark:text-white">
            Include Past Documents and Reports
          </h4>
          <PastDocumentSelector
            customerId={customerId}
            projectId={projectId}
            onDocumentsSelected={handlePastDocumentsSelected}
            onReportsSelected={handlePastReportsSelected}
            selectedDocuments={selectedPastDocuments}
            selectedReports={selectedPastReports}
          />
        </div>
      )}

      {/* Context Input - matching AdditionalContextStep styling */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Additional Instructions or Context (Optional)
        </label>

        <textarea
          value={additionalContext}
          onChange={(e) => setAdditionalContext(e.target.value)}
          placeholder="Add any specific instructions or context for the analysis..."
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                   focus:outline-none focus:ring-2 focus:ring-blue-500
                   dark:bg-gray-700 dark:text-white resize-vertical"
          disabled={effectiveLoading}
        />

        <div className="text-sm text-gray-500 dark:text-gray-400">
          Provide any specific instructions, focus areas, or context that should be considered during the analysis.
          You can also select past documents and reports above to include their content in the analysis.
        </div>
      </div>

      {/* Additional Document Upload - using FileUploader like AdditionalContextStep */}
      {customerId && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 dark:text-white">
            Upload Additional Documents (Optional)
          </h4>
          <FileUploader
            onFilesUploaded={(files) => setAdditionalFiles(files)}
            customerId={customerId}
            projectId={projectId}
            acceptedTypes={STANDARD_ACCEPTED_TYPES}
            maxFiles={10}
            maxFileSize={25}
          />
        </div>
      )}

      {/* Report Title Input */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Report Title (Optional)
        </label>
        <input
          type="text"
          value={reportTitle || ''}
          onChange={(e) => onReportTitleChange?.(e.target.value)}
          placeholder="Leave blank to auto-generate title"
          maxLength={200}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                   focus:outline-none focus:ring-2 focus:ring-blue-500
                   dark:bg-gray-700 dark:text-white"
          disabled={effectiveLoading}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Customize the report title or leave blank to use the default format
        </p>
      </div>

      {/* Generate Report Button */}
      <div className="flex justify-center">
        <button
          onClick={handleSubmit}
          disabled={effectiveLoading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-8 rounded-lg flex items-center gap-2 transition-colors"
        >
          {effectiveLoading ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Generating Analysis...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Generate Analysis Report
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default WorkflowModal;